import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Grid,
  IconButton,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useAuth } from '../../contexts/AuthContext';
import { findStraightCap } from './collectorUtils';
import {
  isFittingCompatibleWithMainPipe,
  isFittingCompatibleWithSquareProfile,
} from './separatorFittingCompatibility';
import {
  HYDRAULIC_SEPARATOR_CLIENT_POWER_STEPS as CLIENT_POWER_STEPS,
  HYDRAULIC_SEPARATOR_CLIENT_POWER_STEPS_MAX as CLIENT_POWER_STEPS_MAX,
  HYDRAULIC_SEPARATOR_CLIENT_PARAMS_TABLE as CLIENT_PARAMS_TABLE,
} from './hydraulicSeparatorClientParams';
import {
  CLIENT_THERMAL_DELTA_TEMP_CHOICES,
  clientUsesPresetDeltaTemp,
  CLIENT_STANDARD_PRODUCTS_DELTA_TEMP_LABEL,
  getHydraulicSeparatorPresetReferenceDeltaTemp,
  normalizeClientThermalDeltaTemp,
} from './clientPresetReferenceDelta';
import {
  CLIENT_STANDARD_PRODUCTS_CONNECTION_LABEL,
  clientUsesPresetConnectionType,
  clientUsesCatalogPresetConnection,
  getHydraulicSeparatorPresetConnectionType,
  getHydraulicSeparatorPresetConnectionSize,
} from './clientPresetConnection';
import {
  getHydraulicSeparatorClientMaxTableFlowM3h,
  isClientFlowM3hOverCatalogTable,
} from './clientPresetCatalogFlow';
import {
  clientCatalogPresetKeyCeilByTableFlowM3h,
  resolveClientConstructionFromThermalInput,
} from './clientConstructionPreset';

function syncClientSeparatorThermalFlowFromPower(newState) {
  const p = Number(newState.power);
  if (!Number.isFinite(p) || p <= 0) return;
  const q = sepCalcFlowFromPower(
    p,
    normalizeClientThermalDeltaTemp(newState.deltaTemp),
    newState.fluid
  );
  if (!Number.isFinite(q) || q <= 0) return;
  newState.flowRate = parseFloat(q.toFixed(2));
}

function resolveClientSeparatorConstructionContext(state) {
  const maxTableQ = getHydraulicSeparatorClientMaxTableFlowM3h(state.shape);
  return resolveClientConstructionFromThermalInput({
    inputMode: state.inputMode,
    powerKw: state.power,
    flowRateM3h: state.flowRate,
    deltaTemp: state.deltaTemp,
    shape: state.shape,
    paramsTable: CLIENT_PARAMS_TABLE,
    powerSteps: CLIENT_POWER_STEPS,
    maxStepKw: CLIENT_POWER_STEPS_MAX,
    maxTableFlowM3h: maxTableQ,
    calcFlowFromPowerKw: (powerKw, dt) => sepCalcFlowFromPower(powerKw, dt, state.fluid),
  });
}

function resolveClientSeparatorPresetKeyFromState(state) {
  const ctx = resolveClientSeparatorConstructionContext(state);
  if (!ctx || ctx.overTable) return null;
  return ctx.constructionKeyKw;
}

function applyClientSeparatorDeltaTempFromConstructionPreset(newState) {
  if (!clientUsesPresetDeltaTemp(newState)) return;
  const key = resolveClientSeparatorPresetKeyFromState(newState);
  if (key == null || !CLIENT_PARAMS_TABLE[key]) return;
  newState.deltaTemp = getHydraulicSeparatorPresetReferenceDeltaTemp(key);
}

function seedClientSeparatorConnectionFromCatalog(newState) {
  const key = resolveClientSeparatorPresetKeyFromState(newState);
  if (key == null || !CLIENT_PARAMS_TABLE[key]) return;
  const connectionType = getHydraulicSeparatorPresetConnectionType(key);
  newState.connectionType = connectionType;
  const sz = getHydraulicSeparatorPresetConnectionSize(key, connectionType);
  if (sz != null) newState.connectionSize = sz;
}

function applyClientSeparatorConnectionFromConstructionPreset(newState) {
  if (!clientUsesPresetConnectionType(newState)) return;
  seedClientSeparatorConnectionFromCatalog(newState);
}

function applyClientSeparatorPowerPresetSnap(newState, clampedPowerKw) {
  if (!Number.isFinite(clampedPowerKw) || clampedPowerKw <= 0) {
    newState.power = clampedPowerKw;
    return;
  }
  const dtSnap = normalizeClientThermalDeltaTemp(newState.deltaTemp);
  const qSnap = sepCalcFlowFromPower(clampedPowerKw, dtSnap, newState.fluid);
  const maxTableQ = getHydraulicSeparatorClientMaxTableFlowM3h(newState.shape);
  if (isClientFlowM3hOverCatalogTable(qSnap, maxTableQ)) {
    newState.power = clampedPowerKw;
    newState.flowRate = parseFloat(qSnap.toFixed(2));
    return;
  }

  newState.power = clampClientSeparatorPowerKw(clampedPowerKw, CLIENT_POWER_STEPS_MAX, newState.shape);
  syncClientSeparatorThermalFlowFromPower(newState);
  applyClientSeparatorDeltaTempFromConstructionPreset(newState);
  applyClientSeparatorConnectionFromConstructionPreset(newState);
}

const SEPARATOR_FLUID_PROPS = {
  water: { density: 1000, specificHeat: 4190 },
  glycol30: { density: 1040, specificHeat: 3930 },
  glycol40: { density: 1050, specificHeat: 3720 },
  glycol50: { density: 1060, specificHeat: 3510 },
};

function sepCalcFlowFromPower(power, deltaTemp, fluid) {
  if (power === '' || power === null || power === undefined || !deltaTemp) return 0;
  const props = SEPARATOR_FLUID_PROPS[fluid] || SEPARATOR_FLUID_PROPS.water;
  const powerWatts = Number(power) * 1000;
  const flowRateM3s = powerWatts / (props.density * props.specificHeat * deltaTemp);
  return flowRateM3s * 3600;
}

function sepCalcPowerFromFlow(flowRate, deltaTemp, fluid) {
  if (!flowRate || !deltaTemp) return 0;
  const props = SEPARATOR_FLUID_PROPS[fluid] || SEPARATOR_FLUID_PROPS.water;
  const flowRateM3s = flowRate / 3600;
  return (flowRateM3s * props.density * props.specificHeat * deltaTemp) / 1000;
}

/** Ключ пресета конструкции по расходу: минимальный табличный Q ≥ ввод. */
function hydraulicSeparatorClientPresetKeyFromFlow(flowNum, _deltaTemp, _fluid, shape) {
  if (!Number.isFinite(flowNum) || flowNum <= 0) return null;
  const maxTableQ = getHydraulicSeparatorClientMaxTableFlowM3h(shape);
  if (isClientFlowM3hOverCatalogTable(flowNum, maxTableQ)) return null;
  return clientCatalogPresetKeyCeilByTableFlowM3h(
    flowNum,
    shape,
    CLIENT_PARAMS_TABLE,
    CLIENT_POWER_STEPS
  );
}

function getHydraulicSeparatorSizingFlowM3h({
  viewMode,
  isClientOverTablePower,
  clientConstructionSizingFlowM3h,
  effectivePower,
  effectiveDeltaTemp,
  inputMode,
  flowRate,
  fluid,
}) {
  if (
    viewMode === 'clients' &&
    !isClientOverTablePower &&
    clientConstructionSizingFlowM3h != null &&
    Number.isFinite(clientConstructionSizingFlowM3h)
  ) {
    return clientConstructionSizingFlowM3h;
  }
  if (inputMode === 'power') {
    return sepCalcFlowFromPower(effectivePower, effectiveDeltaTemp, fluid);
  }
  return flowRate || 0;
}

const SEPARATOR_ENGINEERS_DEFAULT_DELTA_TEMP = 25;

/** Ограничение диапазона кВт без привязки к шагам каталога (40…1000). */
function clampClientSeparatorPowerKw(raw, maxP, shape) {
  const minP = CLIENT_POWER_STEPS[0];
  let clamped = Math.min(Math.max(raw, minP), maxP);
  if (shape === 'square' && clamped > 600) clamped = 600;
  return clamped;
}

/**
 * Ключ круглого корпуса в форме «наружныйДиаметр-толщина» (без «x», в отличие от квадрата).
 */
function parseRoundSeparatorOuterDiameter(separatorKey) {
  if (separatorKey == null || String(separatorKey).includes('x')) return null;
  const parts = String(separatorKey).split('-');
  if (parts.length < 2) return null;
  const od = parseFloat(parts[0]);
  return Number.isNaN(od) ? null : od;
}

const HYDRAULIC_SEPARATOR_CHARACTERISTICS_VIEW_MODE_KEY = 'hydraulicSeparatorCharacteristicsViewMode';

function initialSeparatorCalculatorViewMode(initialParams) {
  const vm = initialParams?._calculatorViewMode;
  if (vm === 'clients' || vm === 'engineers') return vm;
  try {
    const saved = localStorage.getItem(HYDRAULIC_SEPARATOR_CHARACTERISTICS_VIEW_MODE_KEY);
    if (saved === 'clients' || saved === 'engineers') return saved;
  } catch (_) {}
  return 'clients';
}

/**
 * Компонент для расчета параметров гидравлического разделителя
 * на основе входных характеристик (расход, температура и т.д.)
 */
function CharacteristicsCalculator({ onBack, onCalculate, onBackToDrawing, initialParams }) {
  const { isAdmin } = useAuth();

  // Режим отображения характеристик: 'clients' — для клиентов (по умолчанию), 'engineers' — для инженеров
  // Переключатель виден только администратору; хранится в параметрах чертежа и в localStorage
  const [viewMode, setViewMode] = useState(() => initialSeparatorCalculatorViewMode(initialParams));

  // Ref для отслеживания последнего обработанного initialParams
  const lastInitialParamsRef = useRef(null);
  
  // Ref для отслеживания, нужно ли сохранять в localStorage
  const shouldSaveToLocalStorage = useRef(true);
  const preservedEngineersDeltaTempRef = useRef(null);
  
  const [characteristics, setCharacteristics] = useState(() => {
    // Если есть параметры из редактора (возврат назад), восстанавливаем характеристики
    if (initialParams && initialParams._characteristics) {
      lastInitialParamsRef.current = initialParams;
      // Не сохраняем в localStorage при первой загрузке из initialParams
      shouldSaveToLocalStorage.current = false;
      return initialParams._characteristics;
    }
    
    // Пытаемся восстановить сохраненные характеристики из localStorage
    try {
      const saved = localStorage.getItem('hydraulicSeparatorCharacteristics');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Проверяем, что все необходимые поля присутствуют
        if (parsed.inputMode && parsed.deltaTemp && parsed.shape && parsed.pressure) {
          return {
            ...parsed,
            clientPresetDeltaTemp:
              parsed.clientPresetDeltaTemp !== undefined
                ? Boolean(parsed.clientPresetDeltaTemp)
                : !Boolean(parsed.clientManualDeltaTemp),
            clientPresetConnectionType:
              parsed.clientPresetConnectionType !== undefined
                ? Boolean(parsed.clientPresetConnectionType)
                : true,
          };
        }
      }
    } catch (e) {
      console.error('Ошибка при восстановлении характеристик:', e);
    }
    
    // Если не удалось восстановить, используем дефолтные значения
    const defaultPower = 85; // кВт — номинал каталога
    const defaultFluid = 'water';
    const defaultRow = CLIENT_PARAMS_TABLE[defaultPower];
    const defaultDeltaTemp = getHydraulicSeparatorPresetReferenceDeltaTemp(defaultPower);
    const defaultFlow = defaultRow?.flowRate ?? 3;

    return {
      inputMode: 'power', // 'flowRate' или 'power' - режим ввода (по умолчанию - по мощности)
      flowRate: parseFloat(Number(defaultFlow).toFixed(2)),
      power: defaultPower,
      deltaTemp: defaultDeltaTemp,
      clientPresetDeltaTemp: true,
      clientPresetConnectionType: true,
      shape: 'round', // форма сечения: 'round' или 'square'
      pressure: 6, // бар — рабочее давление; для круга 6/8 задаёт тип заглушки (прямая / эллиптическая), для квадрата 4
      fluid: defaultFluid, // вид теплоносителя
      connectionsCount: 4, // количество подключений (по 2 с каждой стороны)
      separatorDiameter: null, // диаметр гидроразделителя (формат "диаметр-толщина", например "60.3-2")
      connectionType: 'nipple', // тип подключений (nipple, flange, и т.д.)
      connectionSize: null, // размер подключений (диаметр в мм, будет установлен автоматически)
    };
  });

  useEffect(() => {
    try {
      localStorage.setItem(HYDRAULIC_SEPARATOR_CHARACTERISTICS_VIEW_MODE_KEY, viewMode);
    } catch (_) {}
  }, [viewMode]);

  useEffect(() => {
    if (viewMode === 'engineers') {
      const saved = preservedEngineersDeltaTempRef.current;
      const nextDt =
        saved != null && CLIENT_THERMAL_DELTA_TEMP_CHOICES.includes(saved)
          ? saved
          : SEPARATOR_ENGINEERS_DEFAULT_DELTA_TEMP;
      setCharacteristics((prev) => {
        if (Number(prev.deltaTemp) === nextDt) return prev;
        return { ...prev, deltaTemp: nextDt };
      });
    }
  }, [viewMode]);

  // Обновляем характеристики при изменении initialParams (возврат из редактора)
  useEffect(() => {
    // Проверяем, что есть новые характеристики
    if (initialParams && initialParams._characteristics) {
      const vm = initialParams._calculatorViewMode;
      if (vm === 'clients' || vm === 'engineers') setViewMode(vm);
      // Сравниваем характеристики по содержимому, а не по ссылке
      const prevCharacteristics = lastInitialParamsRef.current?._characteristics;
      const newCharacteristics = initialParams._characteristics;
      
      // Проверяем, изменились ли характеристики
      const hasChanged = !prevCharacteristics || 
        JSON.stringify(prevCharacteristics) !== JSON.stringify(newCharacteristics);
      
      if (hasChanged) {
        lastInitialParamsRef.current = initialParams;
        setCharacteristics(newCharacteristics);
      }
    } else if (!initialParams) {
      // Если initialParams стал null, сбрасываем ref
      lastInitialParamsRef.current = null;
    }
  }, [initialParams]);

  // Сохраняем характеристики в localStorage при каждом изменении
  useEffect(() => {
    // Пропускаем первое сохранение, если данные пришли из initialParams
    if (!shouldSaveToLocalStorage.current) {
      shouldSaveToLocalStorage.current = true;
      return;
    }
    
    try {
      localStorage.setItem('hydraulicSeparatorCharacteristics', JSON.stringify(characteristics));
    } catch (e) {
      console.error('Ошибка при сохранении характеристик:', e);
    }
  }, [characteristics]);

  const clientConstructionCtx = useMemo(() => {
    if (viewMode !== 'clients') return null;
    return resolveClientSeparatorConstructionContext({
      inputMode: characteristics.inputMode,
      power: characteristics.power,
      flowRate: characteristics.flowRate,
      deltaTemp: characteristics.deltaTemp,
      fluid: characteristics.fluid,
      shape: characteristics.shape,
    });
  }, [
    viewMode,
    characteristics.inputMode,
    characteristics.power,
    characteristics.flowRate,
    characteristics.deltaTemp,
    characteristics.fluid,
    characteristics.shape,
  ]);

  const clientPresetPowerKey =
    clientConstructionCtx && !clientConstructionCtx.overTable
      ? clientConstructionCtx.constructionKeyKw
      : null;

  const clientCatalogConnectionPresetOn =
    viewMode === 'clients' &&
    clientUsesCatalogPresetConnection(characteristics, clientConstructionCtx);

  const clientConstructionSizingFlowM3h = clientConstructionCtx
    ? clientConstructionCtx.sizingFlowM3h
    : null;

  /** Ключ пресета конструкции (кВт) для габаритов каталога; при сверх таблицы — фактическая мощность. */
  const effectivePower = useMemo(() => {
    if (viewMode !== 'clients') {
      return characteristics.power;
    }
    if (clientConstructionCtx?.overTable) {
      if (characteristics.inputMode === 'power') {
        const pn = Number(characteristics.power);
        return Number.isFinite(pn) ? pn : characteristics.power;
      }
      const qn = Number(characteristics.flowRate);
      if (Number.isFinite(qn) && qn > 0) {
        return sepCalcPowerFromFlow(
          qn,
          normalizeClientThermalDeltaTemp(characteristics.deltaTemp),
          characteristics.fluid
        );
      }
      return characteristics.power;
    }
    if (clientPresetPowerKey != null) return clientPresetPowerKey;
    return characteristics.power;
  }, [
    viewMode,
    characteristics.power,
    characteristics.inputMode,
    characteristics.flowRate,
    characteristics.deltaTemp,
    characteristics.fluid,
    clientConstructionCtx,
    clientPresetPowerKey,
  ]);

  const maxClientTableFlowM3h = useMemo(
    () => getHydraulicSeparatorClientMaxTableFlowM3h(characteristics.shape),
    [characteristics.shape]
  );

  const clientThermalFlowM3h = useMemo(() => {
    if (viewMode !== 'clients') return null;
    if (characteristics.inputMode === 'power') {
      const p = characteristics.power;
      const pn = typeof p === 'number' && !Number.isNaN(p) ? p : Number(p);
      if (!Number.isFinite(pn) || pn <= 0) return null;
      return sepCalcFlowFromPower(
        pn,
        normalizeClientThermalDeltaTemp(characteristics.deltaTemp),
        characteristics.fluid
      );
    }
    const qn = Number(characteristics.flowRate);
    return Number.isFinite(qn) && qn > 0 ? qn : null;
  }, [
    viewMode,
    characteristics.inputMode,
    characteristics.power,
    characteristics.flowRate,
    characteristics.deltaTemp,
    characteristics.fluid,
  ]);

  // Клиент, расход выше табличного каталога: свободный P↔Q и подбор по скоростям
  const isClientOverTablePower = useMemo(() => {
    if (viewMode !== 'clients' || clientThermalFlowM3h == null) return false;
    return isClientFlowM3hOverCatalogTable(clientThermalFlowM3h, maxClientTableFlowM3h);
  }, [viewMode, clientThermalFlowM3h, maxClientTableFlowM3h]);

  const clientSizingLockedByPreset =
    viewMode === 'clients' &&
    !isClientOverTablePower &&
    clientPresetPowerKey != null &&
    Boolean(CLIENT_PARAMS_TABLE[clientPresetPowerKey]);

  const CLIENT_CORPUS_TARGET_VELOCITY = 0.21;
  const CLIENT_FLANGE_TARGET_VELOCITY = 1.0;
  const effectiveDeltaTemp = normalizeClientThermalDeltaTemp(characteristics.deltaTemp);

  const connCorpusSeparatorDiameter = characteristics.separatorDiameter;
  const connCorpusShape = characteristics.shape;

  /** Корпус для лимитов подключений: из state или строки пресета (UI может показывать ⌀ без записи в state). */
  const clientSeparatorCorpusForConnections = useMemo(() => {
    if (connCorpusShape === 'round') {
      if (connCorpusSeparatorDiameter) {
        const od = parseFloat(String(connCorpusSeparatorDiameter).split('-')[0]);
        if (Number.isFinite(od)) return { roundMm: od, squareProfile: null };
      }
      if (viewMode === 'clients' && clientPresetPowerKey != null) {
        const d = CLIENT_PARAMS_TABLE[clientPresetPowerKey]?.diameter;
        if (d != null) return { roundMm: Number(d), squareProfile: null };
      }
    } else {
      let prof = connCorpusSeparatorDiameter
        ? String(connCorpusSeparatorDiameter).split('-')[0]
        : null;
      if (!prof && viewMode === 'clients' && clientPresetPowerKey != null) {
        prof = CLIENT_PARAMS_TABLE[clientPresetPowerKey]?.squareProfile ?? null;
      }
      if (prof) return { roundMm: null, squareProfile: prof };
    }
    return { roundMm: null, squareProfile: null };
  }, [connCorpusSeparatorDiameter, connCorpusShape, viewMode, clientPresetPowerKey]);

  /** Расход для скорости в боковых подключениях (при ручной ΔT — фактический Q, не табличный). */
  const getEndConnectionSizingFlowM3h = () => {
    if (viewMode === 'clients' && clientThermalFlowM3h != null) {
      if (isClientOverTablePower || !clientUsesPresetDeltaTemp(characteristics)) {
        return clientThermalFlowM3h;
      }
    }
    return getHydraulicSeparatorSizingFlowM3h({
      viewMode,
      isClientOverTablePower,
      clientConstructionSizingFlowM3h,
      effectivePower,
      effectiveDeltaTemp,
      inputMode: characteristics.inputMode,
      flowRate: characteristics.flowRate,
      fluid: characteristics.fluid,
    });
  };

  /**
   * Доступные размеры подключений (диаметры в мм)
   * Основаны на стандартных размерах фитингов из каталога
   */
  // Размеры подключений из каталога fittingsData
  // Для каждого типа подключения указаны только реально существующие размеры
  const connectionSizes = [
    { diameter: 13.7, displayName: '1/4"', nominalDiameter: null },      // Ниппели, Муфты
    { diameter: 17.2, displayName: '3/8"', nominalDiameter: null },      // Ниппели, Муфты
    { diameter: 21.3, displayName: '1/2"', nominalDiameter: null },      // Ниппели, Муфты
    { diameter: 26.9, displayName: '3/4"', nominalDiameter: null },      // Ниппели, Муфты
    { diameter: 33.7, displayName: '1"', nominalDiameter: null },        // Ниппели, Муфты, Накидные гайки, Шайбы
    { diameter: 42.4, displayName: '1 1/4"', nominalDiameter: null },    // Ниппели, Муфты, Накидные гайки, Шайбы
    { diameter: 48.3, displayName: '1 1/2"', nominalDiameter: null },    // Ниппели, Муфты, Накидные гайки, Шайбы
    { diameter: 60.3, displayName: '2"', nominalDiameter: null },        // Ниппели, Муфты, Накидные гайки, Шайбы
    { diameter: 76.1, displayName: '2 1/2"', nominalDiameter: null },    // Только Ниппели
    { diameter: 88.9, displayName: '3"', nominalDiameter: null },        // Только Ниппели
    { diameter: 114.3, displayName: '4"', nominalDiameter: null },       // Только Ниппели
    // Фланцы (номинальные диаметры Ду)
    { diameter: 25, displayName: 'Ду25', nominalDiameter: 25, isFlangeSize: true },
    { diameter: 32, displayName: 'Ду32', nominalDiameter: 32, isFlangeSize: true },
    { diameter: 40, displayName: 'Ду40', nominalDiameter: 40, isFlangeSize: true },
    { diameter: 50, displayName: 'Ду50', nominalDiameter: 50, isFlangeSize: true },
    { diameter: 65, displayName: 'Ду65', nominalDiameter: 65, isFlangeSize: true },
    { diameter: 80, displayName: 'Ду80', nominalDiameter: 80, isFlangeSize: true },
    { diameter: 100, displayName: 'Ду100', nominalDiameter: 100, isFlangeSize: true },
    { diameter: 125, displayName: 'Ду125', nominalDiameter: 125, isFlangeSize: true },
    { diameter: 150, displayName: 'Ду150', nominalDiameter: 150, isFlangeSize: true },
    { diameter: 200, displayName: 'Ду200', nominalDiameter: 200, isFlangeSize: true },
    { diameter: 250, displayName: 'Ду250', nominalDiameter: 250, isFlangeSize: true },
    { diameter: 300, displayName: 'Ду300', nominalDiameter: 300, isFlangeSize: true },
    { diameter: 350, displayName: 'Ду350', nominalDiameter: 350, isFlangeSize: true },
  ];

  /**
   * Диаметры для геометрии/переходов: гайка, шайба, наружный корпус муфты, труба под фланец.
   * Скорость в муфте при подключении источника считается как у ниппеля того же ряда
   * (см. getVelocityEffectiveOuterDiameter).
   */
  const getTransitionPipeDiameter = (connectionType, nominalDiameter) => {
    if (connectionType === 'nipple') {
      // Ниппели - используем номинальный диаметр трубы как есть
      return nominalDiameter;
    } else if (connectionType === 'coupling') {
      // Муфты — наружный диаметр корпуса (межосевое/вылеты); не для расчёта скорости потока
      const MUFTA_OUTER_DIAMETER = {
        13.7: 17.4,   // 1/4" муфта
        17.2: 20.8,   // 3/8" муфта
        21.3: 24.5,   // 1/2" муфта
        26.9: 29.9,   // 3/4" муфта
        33.7: 38,     // 1" муфта
        42.4: 48,     // 1 1/4" муфта
        48.3: 53.9,   // 1 1/2" муфта
        60.3: 66,     // 2" муфта
      };
      return MUFTA_OUTER_DIAMETER[nominalDiameter] || nominalDiameter;
    } else if (connectionType === 'nut') {
      // Накидные гайки
      const NAK_GAIKA_TO_PIPE_DIAMETER = {
        33.7: 21.3,   // 1"
        42.4: 32,     // 1 1/4"
        48.3: 32,     // 1 1/2"
        60.3: 42.4,   // 2"
      };
      return NAK_GAIKA_TO_PIPE_DIAMETER[nominalDiameter] || nominalDiameter;
    } else if (connectionType === 'washer') {
      // Шайбы с наружной резьбой
      const SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER = {
        33.7: 26.9,   // 1"
        42.4: 32,     // 1 1/4"
        48.3: 32,     // 1 1/2"
        60.3: 42.4,   // 2"
      };
      return SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER[nominalDiameter] || nominalDiameter;
    } else if (connectionType === 'flange') {
      // Фланцы - используем диаметр трубы под фланец
      const DN_TO_PIPE_DIAMETER = {
        25: 33.7,
        32: 42.4, 40: 48.3, 50: 60.3, 65: 76.1,
        80: 88.9, 100: 114.3, 125: 139.7, 150: 168.3,
        200: 219.1, 250: 273, 300: 323.9, 350: 355.6,
      };
      return DN_TO_PIPE_DIAMETER[nominalDiameter] || nominalDiameter;
    }
    return nominalDiameter;
  };

  /** Наружный ⌀ для расчёта скорости в подключении источника; муфта — как ниппель того же ряда. */
  const getVelocityEffectiveOuterDiameter = (connectionType, sizeData) => {
    const { diameter, isFlangeSize, nominalDiameter } = sizeData;
    if (connectionType === 'flange' && isFlangeSize) {
      return getTransitionPipeDiameter(connectionType, nominalDiameter);
    }
    if (connectionType === 'coupling') {
      return getTransitionPipeDiameter('nipple', diameter);
    }
    return getTransitionPipeDiameter(connectionType, diameter);
  };

  const getVelocityEffectiveOuterDiameterByKey = (connectionType, sizeKey) => {
    const sizeData = connectionSizes.find((s) => s.diameter === sizeKey);
    if (sizeData) {
      return getVelocityEffectiveOuterDiameter(connectionType, sizeData);
    }
    if (connectionType === 'coupling') {
      return getTransitionPipeDiameter('nipple', sizeKey);
    }
    return getTransitionPipeDiameter(connectionType, sizeKey);
  };

  /**
   * Получение толщины стенки для типа подключения
   */
  const getConnectionWallThickness = (connectionType) => {
    switch (connectionType) {
      case 'nipple':   // Ниппели
      case 'coupling': // Муфты
        return 3;
      case 'flange':   // Фланцы
      case 'nut':      // Накидные гайки
      case 'washer':   // Шайбы с нар. резьбой
        return 2;
      default:
        return 2;
    }
  };

  /**
   * Преобразование типа и размера подключения в ID фитинга (как в расчёте параметров).
   */
  const getFittingIdFromConnection = (connectionType, connectionSize) => {
    if (!connectionSize) return 'Nippel_1';

    const sizeToSuffix = {
      13.7: '1.4',
      17.2: '3.8',
      21.3: '1.2',
      26.9: '3.4',
      33.7: '1',
      42.4: '1_1.4',
      48.3: '1_1.2',
      60.3: '2',
      76.1: '2_1/2',
      88.9: '3',
      114.3: '4',
    };

    if (connectionType === 'flange') {
      return `Flanec_${connectionSize}_galv`;
    }

    const suffix = sizeToSuffix[connectionSize];
    if (!suffix) return 'Nippel_1';

    if (connectionType === 'nipple') {
      return `Nippel_${suffix}`;
    }
    if (connectionType === 'coupling') {
      return `Mufta_${suffix}`;
    }
    if (connectionType === 'nut') {
      return `Nak_Gaika_${suffix}`;
    }
    if (connectionType === 'washer') {
      return `Shaib_Nar_Rezba_${suffix}`;
    }

    return 'Nippel_1';
  };

  /**
   * Получение максимально допустимого диаметра подключения
   * на основе диаметра гидроразделителя и типа подключения
   */
  const getMaxConnectionDiameter = (separatorDiameterValue, connectionType) => {
    let mainDiameter;
    if (typeof separatorDiameterValue === 'number' && !Number.isNaN(separatorDiameterValue)) {
      mainDiameter = separatorDiameterValue;
    } else if (typeof separatorDiameterValue === 'string' && !separatorDiameterValue.includes('x')) {
      const parts = separatorDiameterValue.split('-');
      mainDiameter = parseFloat(parts[0]);
    } else {
      mainDiameter = NaN;
    }

    if (mainDiameter == null || Number.isNaN(mainDiameter)) {
      return 0;
    }

    // Логика ограничений на основе существующих правил в HydraulicSeparatorEditor
    if (mainDiameter <= 42.4) {
      if (connectionType === 'nipple') return 26.9; // max 3/4"
      if (connectionType === 'coupling') return 21.3; // max 1/2"
      if (connectionType === 'flange') return 0; // фланцы запрещены
      if (connectionType === 'nut') return 33.7; // max 1"
      if (connectionType === 'washer') return 33.7; // max 1"
    }
    
    if (mainDiameter <= 48.3) {
      if (connectionType === 'nipple') return 26.9; // max 3/4"
      if (connectionType === 'coupling') return 26.9; // max 3/4"
      if (connectionType === 'flange') return 0; // фланцы запрещены
      if (connectionType === 'nut') return 33.7; // max 1"
      if (connectionType === 'washer') return 33.7; // max 1"
    }
    
    if (mainDiameter <= 60.3) {
      if (connectionType === 'nipple') return 33.7; // max 1"
      if (connectionType === 'coupling') return 33.7; // max 1"
      if (connectionType === 'flange') return 0; // фланцы запрещены
      if (connectionType === 'nut') return 48.3; // max 1 1/2"
      if (connectionType === 'washer') return 48.3; // max 1 1/2"
    }
    
    if (mainDiameter <= 76.1) {
      if (connectionType === 'nipple') return 48.3; // max 1 1/2"
      if (connectionType === 'coupling') return 42.4; // max 1 1/4"
      if (connectionType === 'flange') return 40; // max Ду40 (для фланцев - номинальный диаметр)
      if (connectionType === 'nut') return 60.3; // max 2"
      if (connectionType === 'washer') return 60.3; // max 2"
    }
    
    if (mainDiameter <= 88.9) {
      if (connectionType === 'nipple') return 60.3; // max 2"
      if (connectionType === 'coupling') return 48.3; // max 1 1/2"
      if (connectionType === 'flange') return 50; // max Ду50
      if (connectionType === 'nut') return 60.3; // max 2"
      if (connectionType === 'washer') return 60.3; // max 2"
    }
    
    if (mainDiameter <= 101.6) {
      if (connectionType === 'nipple') return 60.3; // max 2"
      if (connectionType === 'coupling') return 60.3; // max 2"
      if (connectionType === 'flange') return 50; // max Ду50
      if (connectionType === 'nut') return 60.3; // max 2"
      if (connectionType === 'washer') return 60.3; // max 2"
    }
    
    if (mainDiameter <= 114.3) {
      if (connectionType === 'nipple') return 76.1; // max 2 1/2"
      if (connectionType === 'coupling') return 60.3; // max 2"
      if (connectionType === 'flange') return 65; // max Ду65
      if (connectionType === 'nut') return 76.1; // max 2 1/2"
      if (connectionType === 'washer') return 76.1; // max 2 1/2"
    }
    
    if (mainDiameter <= 129) {
      if (connectionType === 'nipple') return 88.9; // max 3"
      if (connectionType === 'coupling') return 60.3; // max 2"
      if (connectionType === 'flange') return 80; // max Ду80
      if (connectionType === 'nut') return 88.9; // max 3"
      if (connectionType === 'washer') return 88.9; // max 3"
    }
    
    if (mainDiameter === 139.7) {
      if (connectionType === 'nipple') return 88.9; // max 3"
      if (connectionType === 'coupling') return 60.3; // max 2"
      if (connectionType === 'flange') return 80; // max Ду80
      if (connectionType === 'nut') return 88.9; // max 3"
      if (connectionType === 'washer') return 88.9; // max 3"
    }
    
    if (mainDiameter === 159 || mainDiameter === 168.3) {
      if (connectionType === 'nipple') return 114.3; // max 4"
      if (connectionType === 'coupling') return 60.3; // max 2"
      if (connectionType === 'flange') return 100; // max Ду100
      if (connectionType === 'nut') return 114.3; // max 4"
      if (connectionType === 'washer') return 114.3; // max 4"
    }
    
    if (mainDiameter === 219.1) {
      if (connectionType === 'nipple') return 114.3; // max 4"
      if (connectionType === 'coupling') return 60.3; // max 2"
      if (connectionType === 'flange') return 125; // max Ду125
      if (connectionType === 'nut') return 114.3; // max 4"
      if (connectionType === 'washer') return 114.3; // max 4"
    }
    
    if (mainDiameter === 273) {
      if (connectionType === 'nipple') return 114.3; // max 4"
      if (connectionType === 'coupling') return 60.3; // max 2"
      if (connectionType === 'flange') return 150; // max Ду150
      if (connectionType === 'nut') return 114.3; // max 4"
      if (connectionType === 'washer') return 114.3; // max 4"
    }
    
    if (mainDiameter === 323.9) {
      if (connectionType === 'nipple') return 114.3; // max 4"
      if (connectionType === 'coupling') return 60.3; // max 2"
      if (connectionType === 'flange') return 200; // max Ду200
      if (connectionType === 'nut') return 114.3; // max 4"
      if (connectionType === 'washer') return 114.3; // max 4"
    }
    
    if (mainDiameter === 355.6) {
      if (connectionType === 'nipple') return 114.3; // max 4"
      if (connectionType === 'coupling') return 60.3; // max 2"
      if (connectionType === 'flange') return 200; // max Ду200
      if (connectionType === 'nut') return 114.3; // max 4"
      if (connectionType === 'washer') return 114.3; // max 4"
    }
    
    if (mainDiameter === 406.4) {
      if (connectionType === 'nipple') return 114.3; // max 4"
      if (connectionType === 'coupling') return 60.3; // max 2"
      if (connectionType === 'flange') return 250; // max Ду250
      if (connectionType === 'nut') return 114.3; // max 4"
      if (connectionType === 'washer') return 114.3; // max 4"
    }
    
    if (mainDiameter === 508 || mainDiameter === 609.6) {
      if (connectionType === 'nipple') return 114.3; // max 4"
      if (connectionType === 'coupling') return 60.3; // max 2"
      if (connectionType === 'flange') return 350; // max Ду350 (все разрешено)
      if (connectionType === 'nut') return 114.3; // max 4"
      if (connectionType === 'washer') return 114.3; // max 4"
    }
    
    // Для остальных диаметров (если появятся новые) разрешены все размеры
    if (connectionType === 'flange') return 350; // max Ду350 для фланцев
    return 114.3; // max 4" для остальных типов подключений
  };

  /**
   * Проверка, доступен ли данный тип подключения для текущих параметров
   * (есть ли хотя бы один подходящий размер)
   */
  const isConnectionTypeAvailable = (typeToCheck) => {
    const { shape } = characteristics;
    const flowRatePerConnection = getEndConnectionSizingFlowM3h();
    const minConnectionVelocity = 0.3; // м/с
    const maxConnectionVelocity = 1.5; // м/с
    const wallThickness = getConnectionWallThickness(typeToCheck);

    let corpusRoundMm = clientSeparatorCorpusForConnections.roundMm;
    let corpusProfile = clientSeparatorCorpusForConnections.squareProfile;
    if (shape === 'round' && corpusRoundMm == null) {
      const opt = getOptimalDiameter();
      corpusRoundMm = opt?.outerDiameter ?? null;
    }
    if (shape === 'square' && !corpusProfile) {
      const opt = getOptimalDiameter();
      corpusProfile = opt?.profile ?? null;
    }

    const maxConnectionDiameter =
      shape === 'round'
        ? (corpusRoundMm != null ? getMaxConnectionDiameter(corpusRoundMm, typeToCheck) : 0)
        : Number.POSITIVE_INFINITY;
    
    // Проверяем, есть ли хотя бы один подходящий размер
    const hasAvailableSize = connectionSizes.some((sizeData) => {
      const { diameter, isFlangeSize, nominalDiameter } = sizeData;
      
      // Для фланцев показываем только фланцевые размеры, для остальных - только трубные
      if (typeToCheck === 'flange' && !isFlangeSize) return false;
      if (typeToCheck !== 'flange' && isFlangeSize) return false;
      
      // Фильтрация по наличию в каталоге для конкретного типа подключения
      if (typeToCheck === 'coupling') {
        if (diameter > 60.3) return false;
      } else if (typeToCheck === 'nut') {
        if (![33.7, 42.4, 48.3, 60.3].includes(diameter)) return false;
      } else if (typeToCheck === 'washer') {
        if (![33.7, 42.4, 48.3, 60.3].includes(diameter)) return false;
      } else if (typeToCheck === 'nipple') {
        if (diameter > 114.3) return false;
      }
      
      const diameterToCompare = (typeToCheck === 'flange' && isFlangeSize) 
        ? nominalDiameter 
        : diameter;
      
      if (diameterToCompare > maxConnectionDiameter) {
        return false;
      }
      
      const effectiveDiameter = getVelocityEffectiveOuterDiameter(typeToCheck, sizeData);

      const idSizeForRule = (typeToCheck === 'flange' && isFlangeSize) ? nominalDiameter : diameter;
      const fittingId = getFittingIdFromConnection(typeToCheck, idSizeForRule);
      if (shape === 'round' && corpusRoundMm != null) {
        if (!isFittingCompatibleWithMainPipe(fittingId, corpusRoundMm)) return false;
      }
      if (shape === 'square' && corpusProfile) {
        if (!isFittingCompatibleWithSquareProfile(fittingId, corpusProfile)) return false;
      }
      
      const velocity = calculateVelocity(flowRatePerConnection, effectiveDiameter, wallThickness);
      return velocity >= minConnectionVelocity && velocity <= maxConnectionVelocity;
    });
    
    return hasAvailableSize;
  };

  /**
   * Фильтрация размеров подключений по допустимым скоростям (0.3 - 1.5 м/с)
   * и соотношению диаметров с гидроразделителем
   */
  const getFilteredConnectionSizes = () => {
    const { connectionType, shape } = characteristics;
    const flowRatePerConnection = getEndConnectionSizingFlowM3h();
    
    const minConnectionVelocity = 0.3; // м/с
    const maxConnectionVelocity = 1.5; // м/с
    
    const wallThickness = getConnectionWallThickness(connectionType);

    let corpusRoundMm = clientSeparatorCorpusForConnections.roundMm;
    let corpusProfile = clientSeparatorCorpusForConnections.squareProfile;
    if (shape === 'round' && corpusRoundMm == null) {
      const opt = getOptimalDiameter();
      corpusRoundMm = opt?.outerDiameter ?? null;
    }
    if (shape === 'square' && !corpusProfile) {
      const opt = getOptimalDiameter();
      corpusProfile = opt?.profile ?? null;
    }

    const maxConnectionDiameter =
      shape === 'round'
        ? (corpusRoundMm != null ? getMaxConnectionDiameter(corpusRoundMm, connectionType) : 0)
        : Number.POSITIVE_INFINITY;
    
    return connectionSizes.filter((sizeData) => {
      const { diameter, isFlangeSize, nominalDiameter } = sizeData;
      
      // Для фланцев показываем только фланцевые размеры, для остальных - только трубные
      if (connectionType === 'flange' && !isFlangeSize) return false;
      if (connectionType !== 'flange' && isFlangeSize) return false;
      
      // Фильтрация по наличию в каталоге для конкретного типа подключения
      if (connectionType === 'coupling') {
        // Муфты: только до 2" (60.3 мм)
        if (diameter > 60.3) return false;
      } else if (connectionType === 'nut') {
        // Накидные гайки: только 1", 1 1/4", 1 1/2", 2"
        if (![33.7, 42.4, 48.3, 60.3].includes(diameter)) return false;
      } else if (connectionType === 'washer') {
        // Шайбы с нар. резьбой: только 1", 1 1/4", 1 1/2", 2"
        if (![33.7, 42.4, 48.3, 60.3].includes(diameter)) return false;
      } else if (connectionType === 'nipple') {
        // Ниппели: до 4" (114.3 мм)
        if (diameter > 114.3) return false;
      }
      
      // Для фланцев используем номинальный диаметр для сравнения
      const diameterToCompare = (connectionType === 'flange' && isFlangeSize) 
        ? nominalDiameter 
        : diameter;
      
      // Фильтр по соотношению с диаметром гидроразделителя
      if (diameterToCompare > maxConnectionDiameter) {
        return false;
      }
      
      const effectiveDiameter = getVelocityEffectiveOuterDiameter(connectionType, sizeData);

      const idSizeForRule = (connectionType === 'flange' && isFlangeSize) ? nominalDiameter : diameter;
      const fittingId = getFittingIdFromConnection(connectionType, idSizeForRule);
      if (shape === 'round' && corpusRoundMm != null) {
        if (!isFittingCompatibleWithMainPipe(fittingId, corpusRoundMm)) return false;
      }
      if (shape === 'square' && corpusProfile) {
        if (!isFittingCompatibleWithSquareProfile(fittingId, corpusProfile)) return false;
      }
      
      // Фильтр по скорости
      const velocity = calculateVelocity(flowRatePerConnection, effectiveDiameter, wallThickness);
      return velocity >= minConnectionVelocity && velocity <= maxConnectionVelocity;
    });
  };

  /**
   * Получение текущей скорости в подключении
   */
  const getCurrentConnectionVelocity = () => {
    const { connectionType, connectionSize } = characteristics;
    const flowRatePerConnection = getEndConnectionSizingFlowM3h();
    
    if (!connectionSize) {
      const filtered = getFilteredConnectionSizes();
      if (filtered.length === 0) return '0.00';
      const middleIndex = Math.floor(filtered.length / 2);
      const optimalDiameter = filtered[middleIndex].diameter;
      const wallThickness = getConnectionWallThickness(connectionType);
      
      const effectiveDiameter = getVelocityEffectiveOuterDiameterByKey(connectionType, optimalDiameter);
      
      const velocity = calculateVelocity(flowRatePerConnection, effectiveDiameter, wallThickness);
      return velocity.toFixed(2);
    }
    
    const wallThickness = getConnectionWallThickness(connectionType);
    
    const effectiveDiameter = getVelocityEffectiveOuterDiameterByKey(connectionType, connectionSize);
    
    const velocity = calculateVelocity(flowRatePerConnection, effectiveDiameter, wallThickness);
    return velocity.toFixed(2);
  };

  /** Скорость в подключении для строки каталога (для подписей в списке размеров). */
  const getConnectionVelocityForCatalogSize = (sizeData) => {
    const { connectionType } = characteristics;
    const actualFlowRate = getEndConnectionSizingFlowM3h();
    const wallThickness = getConnectionWallThickness(connectionType);
    const effectiveDiameter = getVelocityEffectiveOuterDiameter(connectionType, sizeData);
    return calculateVelocity(actualFlowRate, effectiveDiameter, wallThickness);
  };

  const updateCharacteristic = (key, value, options) => {
    setCharacteristics(prev => {
      const newState = { ...prev, [key]: value };

      if (viewMode === 'clients' && key === 'power' && options?.finalizeClientPreset) {
        const p = typeof newState.power === 'number' ? newState.power : parseFloat(newState.power);
        if (Number.isFinite(p)) {
          applyClientSeparatorPowerPresetSnap(newState, p);
        }
      }

      if (key === 'deltaTemp' && viewMode === 'engineers') {
        const dt = Number(value);
        if (CLIENT_THERMAL_DELTA_TEMP_CHOICES.includes(dt)) {
          preservedEngineersDeltaTempRef.current = dt;
        }
      }

      if (viewMode === 'clients' && key === 'clientPresetDeltaTemp') {
        newState.clientPresetDeltaTemp = Boolean(value);
        if (newState.inputMode === 'power') {
          syncClientSeparatorThermalFlowFromPower(newState);
        }
        applyClientSeparatorDeltaTempFromConstructionPreset(newState);
      }

      if (viewMode === 'clients' && key === 'clientPresetConnectionType') {
        newState.clientPresetConnectionType = Boolean(value);
        if (newState.clientPresetConnectionType) {
          applyClientSeparatorConnectionFromConstructionPreset(newState);
        } else {
          seedClientSeparatorConnectionFromCatalog(newState);
        }
      }

      if (viewMode === 'clients' && key === 'deltaTemp') {
        if (clientUsesPresetDeltaTemp(newState)) {
          return prev;
        }
        newState.deltaTemp = normalizeClientThermalDeltaTemp(Number(value));
        if (newState.inputMode === 'power') {
          syncClientSeparatorThermalFlowFromPower(newState);
        }
      }

      if (viewMode === 'clients' && key === 'flowRate') {
        const qn =
          typeof newState.flowRate === 'number'
            ? newState.flowRate
            : parseFloat(newState.flowRate);
        if (Number.isFinite(qn) && qn > 0) {
          const presetKey = hydraulicSeparatorClientPresetKeyFromFlow(
            qn,
            normalizeClientThermalDeltaTemp(newState.deltaTemp),
            newState.fluid,
            newState.shape
          );
          if (presetKey != null && clientUsesPresetDeltaTemp(newState)) {
            newState.deltaTemp = getHydraulicSeparatorPresetReferenceDeltaTemp(presetKey);
          }
        }
      }
      
      // Если переключили на круглое сечение, устанавливаем давление 6 бар по умолчанию
      if (key === 'shape' && value === 'round') {
        newState.pressure = 6;
      }
      
      // Если переключили на квадратное сечение, устанавливаем давление 4 бар по умолчанию
      if (key === 'shape' && value === 'square') {
        newState.pressure = 4;
        if (newState.inputMode === 'power' && typeof newState.power === 'number' && !Number.isNaN(newState.power)) {
          const lim =
            viewMode === 'clients' ? 600 : computeEngineerMaxThermalPowerKw(newState);
          if (newState.power > lim) {
            newState.power = lim;
          }
        }
      }
      
      // Если изменили deltaTemp, проверяем, не превышает ли мощность новый максимум
      if (key === 'deltaTemp' && newState.inputMode === 'power') {
        const tempChar = { ...newState };
        let maxPower;
        if (viewMode === 'engineers') {
          maxPower = computeEngineerMaxThermalPowerKw(tempChar);
        } else if (tempChar.shape === 'square') {
          maxPower = 600;
        } else {
          const maxDiameter = allDiameters[allDiameters.length - 1];
          const innerDiameter = maxDiameter.outerDiameter - 2 * maxDiameter.wallThickness;
          const area = Math.PI * Math.pow(innerDiameter / 1000 / 2, 2);
          const maxFlowRateM3h = area * 0.25 * 3600;
          maxPower = Math.floor(
            calculatePowerFromFlowRate(maxFlowRateM3h, tempChar.deltaTemp, tempChar.fluid)
          );
        }
        if (typeof newState.power === 'number' && !Number.isNaN(newState.power) && newState.power > maxPower) {
          newState.power = maxPower;
        }
      }

      if (
        key === 'connectionType' &&
        viewMode === 'engineers' &&
        newState.shape === 'square' &&
        newState.inputMode === 'power'
      ) {
        const maxP = computeEngineerMaxThermalPowerKw(newState);
        if (typeof newState.power === 'number' && !Number.isNaN(newState.power) && newState.power > maxP) {
          newState.power = maxP;
        }
      }
      
      if (key === 'inputMode' && value === 'flowRate') {
        if (viewMode === 'clients') {
          const dt = normalizeClientThermalDeltaTemp(prev.deltaTemp);
          const pRaw = prev.power;
          const pn =
            typeof pRaw === 'number' && !Number.isNaN(pRaw) && pRaw > 0 ? pRaw : Number(pRaw);
          newState.flowRate = parseFloat(
            calculateFlowRateFromPower(
              Number.isFinite(pn) && pn > 0 ? pn : 0,
              dt,
              prev.fluid
            ).toFixed(2)
          );
        } else {
          const calculatedFlowRate = calculateFlowRateFromPower(prev.power, prev.deltaTemp, prev.fluid);
          newState.flowRate = parseFloat(calculatedFlowRate.toFixed(2));
        }
      }

      if (key === 'inputMode' && value === 'power') {
        if (viewMode === 'clients') {
          const dt = normalizeClientThermalDeltaTemp(prev.deltaTemp);
          const calculatedPower = calculatePowerFromFlowRate(prev.flowRate, dt, prev.fluid);
          const maxP =
            prev.shape === 'square' ? 600 : CLIENT_POWER_STEPS_MAX;
          newState.power = clampClientSeparatorPowerKw(
            parseFloat(calculatedPower.toFixed(2)),
            maxP,
            prev.shape
          );
          syncClientSeparatorThermalFlowFromPower(newState);
          applyClientSeparatorDeltaTempFromConstructionPreset(newState);
          applyClientSeparatorConnectionFromConstructionPreset(newState);
        } else {
          const calculatedPower = calculatePowerFromFlowRate(prev.flowRate, prev.deltaTemp, prev.fluid);
          newState.power = parseFloat(calculatedPower.toFixed(2));
        }
      }

      // Круглый корпус: в каталоге нет прямой заглушки (только эллипт.) → 8/10 бар
      if (newState.shape === 'round') {
        const od = parseRoundSeparatorOuterDiameter(newState.separatorDiameter);
        if (od != null && !findStraightCap(od)) {
          newState.pressure = 8;
        }
      }
      
      return newState;
    });
  };

  // Клиенты: ⌀/профиль корпуса в state по строке пресета (для подключений и расчёта)
  useEffect(() => {
    if (!clientSizingLockedByPreset || clientPresetPowerKey == null) return;
    const cp = CLIENT_PARAMS_TABLE[clientPresetPowerKey];
    if (!cp) return;
    const key =
      characteristics.shape === 'square'
        ? `${cp.squareProfile}-${cp.squareWallThickness ?? 2}`
        : `${cp.diameter}-${cp.wallThickness}`;
    if (!key || key.includes('undefined')) return;
    setCharacteristics((prev) =>
      prev.separatorDiameter === key ? prev : { ...prev, separatorDiameter: key }
    );
  }, [clientSizingLockedByPreset, clientPresetPowerKey, characteristics.shape]);

  // Свыше каталога при «По стандартным изделиям» — только фланец, размер подбирается по расходу
  useEffect(() => {
    if (viewMode !== 'clients' || !isClientOverTablePower) return;
    if (!clientUsesPresetConnectionType(characteristics)) return;
    if (characteristics.connectionType === 'flange') return;
    if (!isConnectionTypeAvailable('flange')) return;
    setCharacteristics((prev) =>
      prev.connectionType === 'flange'
        ? prev
        : { ...prev, connectionType: 'flange', connectionSize: null }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewMode,
    isClientOverTablePower,
    characteristics.clientPresetConnectionType,
    characteristics.connectionType,
    characteristics.separatorDiameter,
    characteristics.shape,
  ]);

  // Автоматически обновляем диаметр при изменении параметров, влияющих на расчет
  useEffect(() => {
    if (clientSizingLockedByPreset) {
      return;
    }
    const currentDiameterAvailable = characteristics.separatorDiameter && 
      (characteristics.shape === 'round' 
        ? getFilteredDiameters().some(d => `${d.outerDiameter}-${d.wallThickness}` === characteristics.separatorDiameter)
        : getFilteredSquareProfiles().some(p => `${p.profile}-${p.wallThickness}` === characteristics.separatorDiameter)
      );
    
    if (currentDiameterAvailable) {
      return;
    }
    
    const optimal = getOptimalDiameter();
    if (optimal) {
      // Для квадратных профилей используем profile, для круглых - outerDiameter
      const key = optimal.profile 
        ? `${optimal.profile}-${optimal.wallThickness}`  // квадратный профиль
        : `${optimal.outerDiameter}-${optimal.wallThickness}`; // круглая труба
        
      setCharacteristics(prev => {
        // Обновляем только если диаметр изменился
        if (prev.separatorDiameter !== key) {
          const next = { ...prev, separatorDiameter: key };
          if (prev.shape === 'round') {
            const od = parseRoundSeparatorOuterDiameter(key);
            if (od != null && !findStraightCap(od)) {
              next.pressure = 8;
            }
          }
          return next;
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [characteristics.inputMode, characteristics.flowRate, characteristics.power, characteristics.deltaTemp, characteristics.shape, viewMode, effectivePower, isClientOverTablePower, clientSizingLockedByPreset]);

  // Восстановление из localStorage / согласование давления с каталогом заглушек
  useEffect(() => {
    if (viewMode !== 'engineers') return;
    setCharacteristics((prev) => {
      if (prev.shape !== 'round') return prev;
      const od = parseRoundSeparatorOuterDiameter(prev.separatorDiameter);
      if (od == null || findStraightCap(od)) return prev;
      if (Number(prev.pressure) === 8) return prev;
      return { ...prev, pressure: 8 };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, characteristics.separatorDiameter, characteristics.shape]);

  // Автоматически обновляем размер подключений при изменении параметров
  useEffect(() => {
    // В таблице размер из строки каталога; свыше таблицы — автоподбор фланца ниже
    if (clientCatalogConnectionPresetOn && !isClientOverTablePower) {
      return;
    }
    const filteredSizes = getFilteredConnectionSizes();
    if (filteredSizes.length > 0) {
      const currentSizeAvailable = filteredSizes.some((s) => s.diameter === characteristics.connectionSize);
      // Для клиентов свыше таблицы под фланец всегда пересчитываем при смене расхода/корпуса:
      // иначе остаётся старый Ду (напр. 80), хотя уже подходят большие номиналы.
      const skipRecalc =
        currentSizeAvailable &&
        !(isClientOverTablePower && characteristics.connectionType === 'flange');
      if (skipRecalc) {
        return;
      }

      let optimalSize;
      if (
        isClientOverTablePower &&
        characteristics.connectionType === 'flange'
      ) {
        const actualFlowRate = getEndConnectionSizingFlowM3h();
        const targetVel = CLIENT_FLANGE_TARGET_VELOCITY;
        const wallThickness = getConnectionWallThickness('flange');
        let best = filteredSizes[0];
        let minDiff = Infinity;
        for (const sizeData of filteredSizes) {
          const nominal = sizeData.isFlangeSize ? sizeData.nominalDiameter : sizeData.diameter;
          const effD = getTransitionPipeDiameter('flange', nominal);
          const v = calculateVelocity(actualFlowRate, effD, wallThickness);
          const diff = Math.abs(v - targetVel);
          if (diff < minDiff) {
            minDiff = diff;
            best = sizeData;
          }
        }
        optimalSize = best.diameter;
      } else {
        const middleIndex = Math.floor(filteredSizes.length / 2);
        optimalSize = filteredSizes[middleIndex].diameter;
      }
      
      setCharacteristics(prev => {
        if (prev.connectionSize !== optimalSize) {
          return {
            ...prev,
            connectionSize: optimalSize
          };
        }
        return prev;
      });
    } else if (characteristics.connectionSize != null) {
      setCharacteristics((prev) =>
        prev.connectionSize != null ? { ...prev, connectionSize: null } : prev
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    characteristics.inputMode,
    characteristics.flowRate,
    characteristics.power,
    characteristics.deltaTemp,
    characteristics.connectionType,
    characteristics.separatorDiameter,
    viewMode,
    effectivePower,
    isClientOverTablePower,
    characteristics.clientPresetConnectionType,
    clientCatalogConnectionPresetOn,
  ]);

  // Автоматически переключаем тип подключения на доступный, если текущий недоступен
  useEffect(() => {
    const currentTypeAvailable = isConnectionTypeAvailable(characteristics.connectionType);
    
    if (!currentTypeAvailable) {
      // Ищем первый доступный тип подключения в порядке приоритета
      const availableTypes = ['nipple', 'coupling', 'flange', 'nut', 'washer'];
      const firstAvailable = availableTypes.find(type => isConnectionTypeAvailable(type));
      
      if (firstAvailable) {
        setCharacteristics(prev => ({
          ...prev,
          connectionType: firstAvailable
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [characteristics.inputMode, characteristics.flowRate, characteristics.power, characteristics.deltaTemp, characteristics.separatorDiameter, viewMode, effectivePower]);

  // Клиенты: тип подключения из пресета конструкции (если включена галочка и расход в каталоге)
  useEffect(() => {
    if (viewMode !== 'clients') return;
    if (!clientCatalogConnectionPresetOn) return;
    if (clientPresetPowerKey == null) return;
    const t = getHydraulicSeparatorPresetConnectionType(clientPresetPowerKey);
    const sz = getHydraulicSeparatorPresetConnectionSize(clientPresetPowerKey, t);
    setCharacteristics((prev) => {
      if (prev.connectionType === t && (sz == null || prev.connectionSize === sz)) return prev;
      return {
        ...prev,
        connectionType: t,
        ...(sz != null ? { connectionSize: sz } : {}),
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewMode,
    clientCatalogConnectionPresetOn,
    clientPresetPowerKey,
    characteristics.separatorDiameter,
  ]);

  /**
   * Пересчет расхода из мощности
   * Формула: Q = P / (c * ρ * ΔT)
   * где P - мощность (Вт), c - теплоемкость (Дж/(кг·°C)), ρ - плотность (кг/м³), ΔT - разница температур (°C)
   */
  const calculateFlowRateFromPower = (power, deltaTemp, fluid) => {
    // Если мощность пустая, возвращаем 0
    if (power === '' || power === null || power === undefined) return 0;
    
    // Теплофизические свойства теплоносителей при средней температуре
    const fluidProperties = {
      water: { density: 1000, specificHeat: 4190 }, // вода: ρ=1000 кг/м³, c=4.19 кДж/(кг·°C)
      glycol30: { density: 1040, specificHeat: 3930 }, // гликоль 30%
      glycol40: { density: 1050, specificHeat: 3720 }, // гликоль 40%
      glycol50: { density: 1060, specificHeat: 3510 }, // гликоль 50%
    };
    
    const props = fluidProperties[fluid] || fluidProperties.water;
    
    if (deltaTemp === 0) return 0;
    
    // P (Вт) = Q (м³/с) * ρ (кг/м³) * c (Дж/(кг·°C)) * ΔT (°C)
    // Q (м³/с) = P (Вт) / (ρ * c * ΔT)
    // Q (м³/ч) = Q (м³/с) * 3600
    const powerWatts = power * 1000; // кВт -> Вт
    const flowRateM3s = powerWatts / (props.density * props.specificHeat * deltaTemp);
    const flowRateM3h = flowRateM3s * 3600;
    
    return flowRateM3h;
  };

  /**
   * Расчет мощности из расхода
   * Формула: P = Q * c * ρ * ΔT
   * где Q - расход (м³/с), c - теплоемкость (Дж/(кг·°C)), ρ - плотность (кг/м³), ΔT - разница температур (°C)
   */
  const calculatePowerFromFlowRate = (flowRate, deltaTemp, fluid) => {
    // Теплофизические свойства теплоносителей при средней температуре
    const fluidProperties = {
      water: { density: 1000, specificHeat: 4190 }, // вода: ρ=1000 кг/м³, c=4.19 кДж/(кг·°C)
      glycol30: { density: 1040, specificHeat: 3930 }, // гликоль 30%
      glycol40: { density: 1050, specificHeat: 3720 }, // гликоль 40%
      glycol50: { density: 1060, specificHeat: 3510 }, // гликоль 50%
    };
    
    const props = fluidProperties[fluid] || fluidProperties.water;
    
    if (deltaTemp === 0) return 0;
    
    // P (Вт) = Q (м³/с) * ρ (кг/м³) * c (Дж/(кг·°C)) * ΔT (°C)
    // P (кВт) = P (Вт) / 1000
    const flowRateM3s = flowRate / 3600; // м³/ч -> м³/с
    const powerWatts = flowRateM3s * props.density * props.specificHeat * deltaTemp;
    const powerKw = powerWatts / 1000;
    
    return powerKw;
  };

  /**
   * Расчет скорости теплоносителя в трубе
   * Формула: v = Q / S, где Q - расход (м³/с), S - площадь сечения (м²)
   * S = π * (D/2)², где D - внутренний диаметр (м)
   */
  const calculateVelocity = (flowRate, outerDiameter, wallThickness = 2) => {
    const flowRateM3s = flowRate / 3600; // м³/ч -> м³/с
    const innerDiameter = outerDiameter - 2 * wallThickness; // мм (внутренний диаметр)
    const innerDiameterM = innerDiameter / 1000; // мм -> м
    const area = Math.PI * Math.pow(innerDiameterM / 2, 2); // м²
    return flowRateM3s / area; // м/с
  };

  /**
   * Расчет скорости теплоносителя в квадратной трубе
   * Формула: v = Q / S, где Q - расход (м³/с), S - площадь сечения
   * S = (a - 2t)², где a - размер стороны профиля (мм), t - толщина стенки (мм)
   */
  const calculateVelocitySquare = (flowRate, profile, wallThickness = 2) => {
    const flowRateM3s = flowRate / 3600; // м³/ч -> м³/с
    // Извлекаем размер из формата "80x80"
    const size = parseInt(profile.split('x')[0]); // мм
    const innerSize = size - 2 * wallThickness; // мм (внутренний размер квадрата)
    const squareArea = (innerSize / 1000) * (innerSize / 1000); // м² (площадь квадрата)
    
    // Скорость = расход / площадь
    return flowRateM3s / squareArea; // м/с
  };

  /**
   * Проверка, есть ли подходящие диаметры для текущих параметров
   */
  const hasAvailableDiameters = () => {
    const filtered = getFilteredDiameters();
    return filtered.length > 0;
  };

  /**
   * Получение текста подсказки, если нет подходящих диаметров
   */
  const getDiameterHintText = () => {
    const squareHint =
      'Для этой мощности нет подходящих квадратных профилей. Попробуйте: круглое сечение или увеличьте ΔT';

    // Только для клиентов: подсказка при мощности на квадрате выше 600 кВт
    if (
      viewMode === 'clients' &&
      characteristics.shape === 'square' &&
      (characteristics.inputMode === 'power' || characteristics.inputMode === 'flowRate')
    ) {
      const pRaw = characteristics.power;
      const p =
        typeof effectivePower === 'number' && !Number.isNaN(effectivePower)
          ? effectivePower
          : typeof pRaw === 'number'
            ? pRaw
            : NaN;
      if (!Number.isNaN(p) && p > 600) {
        return squareHint;
      }
    }

    if (hasAvailableDiameters()) return null;

    if (characteristics.shape === 'square') {
      // Клиенты, квадрат, ввод по мощности: до 600 кВт включительно не показываем «нет профилей»
      // (лимит по продукту; расхождение с фильтром каталога не считаем ошибкой)
      if (
        viewMode === 'clients' &&
        (characteristics.inputMode === 'power' || characteristics.inputMode === 'flowRate')
      ) {
        const p =
          typeof effectivePower === 'number' && !Number.isNaN(effectivePower)
            ? effectivePower
            : typeof characteristics.power === 'number'
              ? characteristics.power
              : NaN;
        if (!Number.isNaN(p) && p <= 600) {
          return null;
        }
      }
      return squareHint;
    }
    return 'Для этой мощности нет подходящих диаметров. Попробуйте увеличить ΔT';
  };

  /**
   * Все доступные диаметры из каталога (>38 мм, исключая 54 и 306)
   * Структура: { outerDiameter: внешний диаметр, wallThickness: толщина стенки }
   * Включаем все варианты толщин, которые есть в каталоге
   */
  const allDiameters = [
    { outerDiameter: 42.4, wallThickness: 2 },
    { outerDiameter: 42.4, wallThickness: 3 },
    { outerDiameter: 48.3, wallThickness: 2 },
    { outerDiameter: 48.3, wallThickness: 3 },
    { outerDiameter: 60.3, wallThickness: 2 },
    { outerDiameter: 60.3, wallThickness: 3 },
    { outerDiameter: 76.1, wallThickness: 2 },
    { outerDiameter: 76.1, wallThickness: 3 },
    { outerDiameter: 88.9, wallThickness: 2 },
    { outerDiameter: 88.9, wallThickness: 3 },
    { outerDiameter: 101.6, wallThickness: 2 },
    { outerDiameter: 114.3, wallThickness: 2 },
    { outerDiameter: 114.3, wallThickness: 3 },
    { outerDiameter: 129, wallThickness: 2 },
    { outerDiameter: 129, wallThickness: 3 },
    { outerDiameter: 139.7, wallThickness: 2 },
    { outerDiameter: 139.7, wallThickness: 3 },
    { outerDiameter: 159, wallThickness: 2 },
    { outerDiameter: 159, wallThickness: 3 },
    { outerDiameter: 168.3, wallThickness: 3 },
    { outerDiameter: 219.1, wallThickness: 3 },
    { outerDiameter: 273, wallThickness: 3 },
    { outerDiameter: 323.9, wallThickness: 3 },
    { outerDiameter: 355.6, wallThickness: 3 },
    { outerDiameter: 406.4, wallThickness: 3 },
    { outerDiameter: 508, wallThickness: 3 },
    { outerDiameter: 609.6, wallThickness: 3 },
  ];

  /**
   * Все доступные квадратные профили из каталога
   * Структура: { profile: размер профиля, wallThickness: толщина стенки }
   */
  const allSquareProfiles = [
    { profile: '40x40', wallThickness: 2 },
    { profile: '50x50', wallThickness: 2 },
    { profile: '60x60', wallThickness: 2 },
    { profile: '80x80', wallThickness: 2 },
    { profile: '100x100', wallThickness: 3 },
    { profile: '150x150', wallThickness: 3 },
  ];

  /** Верхняя скорость в корпусе гидроразделителя при оценке max мощности (как getFiltered*). */
  const SEPARATOR_CORPUS_MAX_VELOCITY_MS = 0.25;
  /** Верхняя скорость в подключении при оценке max расхода (как getFilteredConnectionSizes). */
  const SEPARATOR_END_MAX_VELOCITY_MS = 1.5;
  /** Профиль квадрата для верхнего лимита мощности у инженеров (макс. из каталога). */
  const SEPARATOR_ENGINEER_MAX_SQUARE_PROFILE = '150x150';

  const getSquareCatalogWallThickness = (profileKey) => {
    const row = allSquareProfiles.find((p) => p.profile === profileKey);
    return row?.wallThickness ?? 2;
  };

  /**
   * Макс. расход (м³/ч) через подключение при 1,5 м/с: макс. корпус (⌀609.6 или 150×150) и правила
   * getMaxConnectionDiameter / separatorFittingCompatibility (как фильтр размеров без привязки к текущему расходу).
   */
  const getSeparatorEngineerMaxEndConnectionFlowM3h = (connectionType, shape) => {
    const wallThickness = getConnectionWallThickness(connectionType);
    const corpusRoundMm = shape === 'round' ? allDiameters[allDiameters.length - 1].outerDiameter : null;
    const corpusProfile = shape === 'square' ? SEPARATOR_ENGINEER_MAX_SQUARE_PROFILE : null;

    const maxConnectionDiameterLimit =
      shape === 'round' && corpusRoundMm != null
        ? getMaxConnectionDiameter(corpusRoundMm, connectionType)
        : Number.POSITIVE_INFINITY;

    let bestQM3h = 0;

    for (const sizeData of connectionSizes) {
      const { diameter, isFlangeSize, nominalDiameter } = sizeData;

      if (connectionType === 'flange' && !isFlangeSize) continue;
      if (connectionType !== 'flange' && isFlangeSize) continue;
      if (connectionType === 'coupling' && diameter > 60.3) continue;
      if (connectionType === 'nut' && ![33.7, 42.4, 48.3, 60.3].includes(diameter)) continue;
      if (connectionType === 'washer' && ![33.7, 42.4, 48.3, 60.3].includes(diameter)) continue;
      if (connectionType === 'nipple' && diameter > 114.3) continue;

      const diameterToCompare =
        connectionType === 'flange' && isFlangeSize ? nominalDiameter : diameter;

      if (diameterToCompare > maxConnectionDiameterLimit) continue;

      const effectiveDiameter = getVelocityEffectiveOuterDiameter(connectionType, sizeData);

      const idSizeForRule = connectionType === 'flange' && isFlangeSize ? nominalDiameter : diameter;
      const fittingId = getFittingIdFromConnection(connectionType, idSizeForRule);

      if (shape === 'round' && corpusRoundMm != null) {
        if (!isFittingCompatibleWithMainPipe(fittingId, corpusRoundMm)) continue;
      }
      if (shape === 'square' && corpusProfile) {
        if (!isFittingCompatibleWithSquareProfile(fittingId, corpusProfile)) continue;
      }

      const innerDiameterMm = effectiveDiameter - 2 * wallThickness;
      if (innerDiameterMm <= 0) continue;
      const areaM2 = Math.PI * Math.pow(innerDiameterMm / 1000 / 2, 2);
      const qM3h = SEPARATOR_END_MAX_VELOCITY_MS * areaM2 * 3600;
      if (qM3h > bestQM3h) bestQM3h = qM3h;
    }

    return bestQM3h;
  };

  /** Верхняя мощность (кВт) для режима инженеров: min(корпус до 0,25 м/с, подключения до 1,5 м/с). */
  const computeEngineerMaxThermalPowerKw = (state) => {
    const { shape, deltaTemp, fluid, connectionType } = state;
    const dt = Number(deltaTemp);
    if (!Number.isFinite(dt) || dt <= 0) return 0;

    let bodyFlowM3h;
    if (shape === 'square') {
      const wt = getSquareCatalogWallThickness(SEPARATOR_ENGINEER_MAX_SQUARE_PROFILE);
      const side = parseInt(SEPARATOR_ENGINEER_MAX_SQUARE_PROFILE.split('x')[0], 10);
      const innerMm = side - 2 * wt;
      const areaM2 = (innerMm / 1000) * (innerMm / 1000);
      bodyFlowM3h = areaM2 * SEPARATOR_CORPUS_MAX_VELOCITY_MS * 3600;
    } else {
      const maxDiameter = allDiameters[allDiameters.length - 1];
      const innerDiameter = maxDiameter.outerDiameter - 2 * maxDiameter.wallThickness;
      const area = Math.PI * Math.pow(innerDiameter / 1000 / 2, 2);
      bodyFlowM3h = area * SEPARATOR_CORPUS_MAX_VELOCITY_MS * 3600;
    }

    let q = bodyFlowM3h;
    if (shape === 'square') {
      const qEnd = getSeparatorEngineerMaxEndConnectionFlowM3h(connectionType, shape);
      if (qEnd > 0) q = Math.min(bodyFlowM3h, qEnd);
    }
    return Math.floor(calculatePowerFromFlowRate(q, dt, fluid));
  };

  /**
   * Получение максимальной доступной мощности (подсказка и clamp поля мощности).
   * Клиенты, квадрат: 600 кВт. Клиенты, круг: только корпус. Инженеры: корпус + подключения.
   */
  const getMaxAvailablePower = () => {
    const { fluid, shape } = characteristics;

    if (shape === 'square' && viewMode === 'clients') {
      return 600;
    }

    if (viewMode === 'engineers') {
      return computeEngineerMaxThermalPowerKw(characteristics);
    }

    const maxDiameter = allDiameters[allDiameters.length - 1];
    const innerDiameter = maxDiameter.outerDiameter - 2 * maxDiameter.wallThickness;
    const area = Math.PI * Math.pow(innerDiameter / 1000 / 2, 2);
    const maxFlowRateM3h = area * SEPARATOR_CORPUS_MAX_VELOCITY_MS * 3600;
    return Math.floor(calculatePowerFromFlowRate(maxFlowRateM3h, effectiveDeltaTemp, fluid));
  };

  // Смена «Для клиентов» / «Для инженеров»: поджать мощность под лимит квадрата (клиенты 600; инженеры — расчётный)
  useEffect(() => {
    if (characteristics.shape !== 'square' || characteristics.inputMode !== 'power') return;
    const lim =
      viewMode === 'clients' ? 600 : computeEngineerMaxThermalPowerKw(characteristics);
    const p = characteristics.power;
    if (typeof p === 'number' && !Number.isNaN(p) && p > lim) {
      setCharacteristics((prev) => ({ ...prev, power: lim }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  /**
   * Фильтрация диаметров по допустимым скоростям (0.09 - 0.25 м/с).
   * Давление не ограничивает каталог труб; оно задаёт только тип заглушки (см. getSeparatorCapTypeFromPressure).
   */
  const getFilteredDiameters = () => {
    const { inputMode, flowRate, fluid, shape } = characteristics;
    if (shape === 'square') {
      return getFilteredSquareProfiles();
    }
    const actualFlowRate = getHydraulicSeparatorSizingFlowM3h({
      viewMode,
      isClientOverTablePower,
      clientConstructionSizingFlowM3h,
      effectivePower,
      effectiveDeltaTemp,
      inputMode,
      flowRate,
      fluid,
    });
    
    // Фильтруем диаметры по скорости (рассчитанной по внутреннему диаметру)
    const minVelocity = 0.09; // м/с
    const maxVelocity = 0.25; // м/с
    
    return allDiameters.filter(({ outerDiameter, wallThickness }) => {
      const velocity = calculateVelocity(actualFlowRate, outerDiameter, wallThickness);
      return velocity >= minVelocity && velocity <= maxVelocity;
    });
  };

  /**
   * Фильтрация квадратных профилей по допустимым скоростям (0.09 - 0.25 м/с)
   */
  const getFilteredSquareProfiles = () => {
    const { inputMode, flowRate, fluid } = characteristics;
    const actualFlowRate = getHydraulicSeparatorSizingFlowM3h({
      viewMode,
      isClientOverTablePower,
      clientConstructionSizingFlowM3h,
      effectivePower,
      effectiveDeltaTemp,
      inputMode,
      flowRate,
      fluid,
    });
    
    // Фильтруем профили по скорости
    const minVelocity = 0.09; // м/с
    const maxVelocity = 0.25; // м/с
    
    return allSquareProfiles.filter(({ profile, wallThickness }) => {
      const velocity = calculateVelocitySquare(actualFlowRate, profile, wallThickness);
      return velocity >= minVelocity && velocity <= maxVelocity;
    });
  };

  /**
   * Получение оптимального диаметра/профиля (ближайшего к скорости 0.15 м/с)
   * При одинаковом внешнем диаметре/профиле предпочитает меньшую толщину стенки
   */
  const getOptimalDiameter = () => {
    const { inputMode, flowRate, fluid, shape } = characteristics;
    const actualFlowRate = getHydraulicSeparatorSizingFlowM3h({
      viewMode,
      isClientOverTablePower,
      clientConstructionSizingFlowM3h,
      effectivePower,
      effectiveDeltaTemp,
      inputMode,
      flowRate,
      fluid,
    });
    
    const filtered = getFilteredDiameters();
    if (filtered.length === 0) return shape === 'square' ? allSquareProfiles[0] : allDiameters[0];
    
    const targetVelocity = isClientOverTablePower ? CLIENT_CORPUS_TARGET_VELOCITY : 0.15; // м/с
    
    // Группируем по диаметру/профилю
    const groupingKey = shape === 'square' ? 'profile' : 'outerDiameter';
    const grouped = {};
    for (const item of filtered) {
      const key = item[groupingKey];
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(item);
    }
    
    // Для каждого диаметра/профиля берем вариант с минимальной толщиной стенки
    const candidatesWithMinWall = Object.values(grouped).map(group => {
      return group.sort((a, b) => a.wallThickness - b.wallThickness)[0];
    });
    
    // Из этих кандидатов выбираем тот, у которого скорость ближе всего к 0.15 м/с
    let optimal = candidatesWithMinWall[0];
    let minDifference;
    
    if (shape === 'square') {
      minDifference = Math.abs(
        calculateVelocitySquare(actualFlowRate, candidatesWithMinWall[0].profile, candidatesWithMinWall[0].wallThickness) - targetVelocity
      );
      
      for (const item of candidatesWithMinWall) {
        const velocity = calculateVelocitySquare(actualFlowRate, item.profile, item.wallThickness);
        const difference = Math.abs(velocity - targetVelocity);
        
        if (difference < minDifference) {
          minDifference = difference;
          optimal = item;
        }
      }
    } else {
      minDifference = Math.abs(
        calculateVelocity(actualFlowRate, candidatesWithMinWall[0].outerDiameter, candidatesWithMinWall[0].wallThickness) - targetVelocity
      );
      
      for (const item of candidatesWithMinWall) {
        const velocity = calculateVelocity(actualFlowRate, item.outerDiameter, item.wallThickness);
        const difference = Math.abs(velocity - targetVelocity);
        
        if (difference < minDifference) {
          minDifference = difference;
          optimal = item;
        }
      }
    }
    
    return optimal;
  };

  /**
   * Получение текущей скорости для выбранного или автоматического диаметра/профиля
   */
  const getCurrentVelocity = () => {
    const { inputMode, flowRate, fluid, separatorDiameter, shape } = characteristics;
    const actualFlowRate = getHydraulicSeparatorSizingFlowM3h({
      viewMode,
      isClientOverTablePower,
      clientConstructionSizingFlowM3h,
      effectivePower,
      effectiveDeltaTemp,
      inputMode,
      flowRate,
      fluid,
    });

    // Если диаметр/профиль не выбран (авто), берем оптимальный (ближайший к целевой скорости в корпусе)
    let item;
    if (!separatorDiameter) {
      item = getOptimalDiameter();
    } else {
      // Разбираем формат "диаметр-толщина" или "профиль-толщина"
      const parts = separatorDiameter.split('-');
      
      if (shape === 'square') {
        const profile = parts[0];
        const wallThickness = Number(parts[1]);
        // Находим данные по выбранному профилю и толщине
        item = allSquareProfiles.find(d => 
          d.profile === profile && d.wallThickness === wallThickness
        ) || allSquareProfiles[0];
      } else {
        const [outerDiameter, wallThickness] = parts.map(Number);
        // Находим данные по выбранному диаметру и толщине
        item = allDiameters.find(d => 
          d.outerDiameter === outerDiameter && d.wallThickness === wallThickness
        ) || allDiameters[0];
      }
    }

    let velocity;
    if (shape === 'square') {
      velocity = calculateVelocitySquare(actualFlowRate, item.profile, item.wallThickness);
    } else {
      velocity = calculateVelocity(actualFlowRate, item.outerDiameter, item.wallThickness);
    }
    
    return velocity.toFixed(2);
  };

  /**
   * Тип торцевой заглушки по давлению (круг): 6/8 бар — прямая, 8/10 бар — эллиптическая.
   * Квадрат — всегда прямая (плоская) заглушка.
   */
  const getSeparatorCapTypeFromPressure = (shape, pressure) => {
    if (shape === 'square') return 'straight';
    const p = Number(pressure);
    return p >= 8 ? 'elliptical' : 'straight';
  };

  /** Подпись для поля «Заглушка» в форме */
  const getSeparatorCapDisplayLabel = (shape, pressure) => {
    if (shape === 'square') {
      return 'Прямая (4/6 бар)';
    }
    const p = Number(pressure);
    return p >= 8 ? 'Эллиптическая (8/10 бар)' : 'Прямая (6/8 бар)';
  };

  /**
   * Получение специальных отступов для конкретных диаметров
   * Возвращает { leftFirst, leftLast, rightFirst, rightLast, spacing, height } или null
   */
  /*
  const getCustomOffsetsForDiameter = (diameter) => {
    // Специальные настройки для конкретных диаметров
    const customSettings = {
      60.3: {
        height: 272, // Высота гидроразделителя
        left: {
          firstOffset: 52,
          lastOffset: 77,
          spacing: 125,
        },
        right: {
          firstOffset: 77,
          lastOffset: 52,
          spacing: 125,
        },
      },
      76.1: {
        height: 324, // Высота гидроразделителя
        left: {
          firstOffset: 70,
          lastOffset: 104,
          spacing: 150,
        },
        right: {
          firstOffset: 104,
          lastOffset: 70,
          spacing: 150,
        },
      },
      88.9: {
        height: 324, // Высота гидроразделителя
        left: {
          firstOffset: 70,
          lastOffset: 104,
          spacing: 150,
        },
        right: {
          firstOffset: 104,
          lastOffset: 70,
          spacing: 150,
        },
      },
      101.6: {
        height: 399, // Высота гидроразделителя
        left: {
          firstOffset: 64,
          lastOffset: 99,
          spacing: 200,
        },
        right: {
          firstOffset: 99,
          lastOffset: 64,
          spacing: 200,
        },
      },
      114.3: {
        height: 400, // Высота гидроразделителя
        left: {
          firstOffset: 100,
          lastOffset: 100,
          spacing: 200,
        },
        right: {
          firstOffset: 100,
          lastOffset: 100,
          spacing: 200,
        },
      },
      129: {
        height: 400, // Высота гидроразделителя
        left: {
          firstOffset: 100,
          lastOffset: 100,
          spacing: 200,
        },
        right: {
          firstOffset: 100,
          lastOffset: 100,
          spacing: 200,
        },
      },
      139.7: {
        height: 450, // Высота гидроразделителя
        left: {
          firstOffset: 125,
          lastOffset: 125,
          spacing: 200,
        },
        right: {
          firstOffset: 125,
          lastOffset: 125,
          spacing: 200,
        },
      },
    };
    
    return customSettings[diameter] || null;
  };
  */

  const calculateParameters = () => {
    const { connectionsCount, separatorDiameter } = characteristics;

    // Для клиентского режима: используем фиксированные параметры из таблицы
    if (viewMode === 'clients' && CLIENT_PARAMS_TABLE[effectivePower]) {
      const cp = CLIENT_PARAMS_TABLE[effectivePower];
      const connectionsPerSide = Math.floor(connectionsCount / 2);
      const isSquare = characteristics.shape === 'square';

      const presetConn = clientCatalogConnectionPresetOn;
      const cpVariant = cp[characteristics.connectionType] || null;
      const cpConnectionType = presetConn
        ? (cpVariant?.connectionType ?? cp.connectionType ?? characteristics.connectionType)
        : characteristics.connectionType;
      const cpConnectionSize = presetConn
        ? (cpVariant?.connectionSize ?? cp.connectionSize ?? characteristics.connectionSize)
        : characteristics.connectionSize;
      const cpLeft  = cpVariant?.left  ?? cp.left;
      const cpRight = cpVariant?.right ?? cp.right;

      // Для квадратного сечения: профиль из таблицы; отступы и межосевое — те же, что для круга (из таблицы)
      const squareProfile = isSquare ? (cp.squareProfile || null) : null;
      const squareWallThickness = isSquare ? (cp.squareWallThickness || 2) : 2;

      const leftCfg = cpLeft;
      const rightCfg = cpRight;

      const buildConnections = (side) => {
        const cfg = side === 'left' ? leftCfg : rightCfg;
        return Array.from({ length: connectionsPerSide }, (_, i) => ({
          id: `${side}-${i + 1}`,
          fittingId: getFittingIdFromConnection(cpConnectionType, cpConnectionSize),
          fittingLength: null,
          offsetFromTop: Math.round(cfg.firstOffset + i * cfg.spacing),
          offsetDirection: 'top',
          spacingToNext: null,
        }));
      };

      // Высота трубы: от верха до центра первого подключения + межосевые + от центра последнего до низа
      const heightFromSide = (cfg) =>
        cfg.firstOffset + Math.max(0, connectionsPerSide - 1) * cfg.spacing + cfg.lastOffset;
      const height = Math.round(Math.max(heightFromSide(leftCfg), heightFromSide(rightCfg)));

      onCalculate({
        shape: isSquare ? 'square' : 'round',
        ...(isSquare
          ? { mainPipeProfile: squareProfile, mainPipeWallThickness: `x${squareWallThickness}` }
          : { mainPipeDiameter: cp.diameter, mainPipeWallThickness: `x${cp.wallThickness}` }
        ),
        separatorHeight: Math.round(height),
        hasTopCap: true,
        hasBottomCap: true,
        topCapElementType: 'cap',
        bottomCapElementType: 'cap',
        topCapType: isSquare ? 'straight' : cp.capType,
        bottomCapType: isSquare ? 'straight' : cp.capType,
        hasTopConnection: true,
        topCapFittingCategory: 'Муфты',
        topCapFitting: cp.topCapFitting,
        topCapFittingLength: null,
        hasBottomConnection: true,
        bottomCapFittingCategory: 'Муфты',
        bottomCapFitting: cp.bottomCapFitting,
        bottomCapFittingLength: null,
        connectionsPlacementMode: 'spacing',
        leftConnectionsFirstOffset: leftCfg.firstOffset,
        leftConnectionsFirstOffsetMode: 'fromEdge',
        leftConnectionsLastOffset: leftCfg.lastOffset,
        leftConnectionsSpacing: leftCfg.spacing,
        leftConnections: buildConnections('left'),
        rightConnectionsFirstOffset: rightCfg.firstOffset,
        rightConnectionsFirstOffsetMode: 'fromEdge',
        rightConnectionsLastOffset: rightCfg.lastOffset,
        rightConnectionsSpacing: rightCfg.spacing,
        rightConnections: buildConnections('right'),
        additionalOutlets: [],
        _calculatedFrom: 'characteristics',
        _calculatorViewMode: viewMode,
        _characteristics: characteristics,
      });
      return;
    }

    // Определяем диаметр и толщину стенки гидроразделителя
    let selectedDiameter;
    let selectedWallThickness;
    
    if (separatorDiameter) {
      // Разбираем формат "диаметр-толщина" или "профиль-толщина"
      const parts = separatorDiameter.split('-');
      selectedDiameter = parseFloat(parts[0]);
      selectedWallThickness = parseInt(parts[1]);
    } else {
      // Автоматический подбор: берем оптимальный диаметр (ближайший к скорости 0.15 м/с)
      const optimal = getOptimalDiameter();
      selectedDiameter = optimal.outerDiameter || parseInt(optimal.profile?.split('x')[0]);
      selectedWallThickness = optimal.wallThickness;
    }
    
    // Проверяем, есть ли специальные настройки для данного диаметра
    // const customSettings = getCustomOffsetsForDiameter(selectedDiameter);
    const customSettings = null; // Временно отключено
    
    let minHeight, leftFirstOffset, leftLastOffset, leftSpacing, rightFirstOffset, rightLastOffset, rightSpacing;
    
    if (customSettings) {
      // Используем специальные настройки
      minHeight = customSettings.height;
      leftFirstOffset = customSettings.left.firstOffset;
      leftLastOffset = customSettings.left.lastOffset;
      leftSpacing = customSettings.left.spacing;
      rightFirstOffset = customSettings.right.firstOffset;
      rightLastOffset = customSettings.right.lastOffset;
      rightSpacing = customSettings.right.spacing;
    } else {
      // Используем стандартный расчет по новой формуле
      
      // Функция для округления в большую сторону с кратностью 5
      const roundUpToMultipleOf5 = (value) => {
        return Math.ceil(value / 5) * 5;
      };
      
      // Определяем диаметр патрубка (подключения)
      const connectionDiameter = characteristics.connectionSize;
      
      // Определяем внутренний диаметр корпуса гидроразделителя
      // Внутренний диаметр = Внешний диаметр - 2 * толщина стенки
      const wallThicknessValue = selectedWallThickness; // это число (2 или 3)
      const innerCorpusDiameter = selectedDiameter - 2 * wallThicknessValue;
      
      // Для патрубка тоже нужен внутренний диаметр
      // Используем ту же логику определения толщины стенки, что и в getConnectionWallThickness
      let connectionWallThickness = 3; // по умолчанию для большинства типов
      if (characteristics.connectionType === 'nut' || characteristics.connectionType === 'washer' || characteristics.connectionType === 'flange') {
        connectionWallThickness = 2; // для накидных гаек, шайб и фланцев
      }
      const innerConnectionDiameter = connectionDiameter - 2 * connectionWallThickness;
      
      // Расчет расстояния между подключениями:
      // 3*d_внутр(патрубка) или 2*D_внутр(корпуса) — выбирайте то, что больше
      const spacingOption1 = 3 * innerConnectionDiameter;
      const spacingOption2 = 2 * innerCorpusDiameter;
      const spacingCalculated = Math.max(spacingOption1, spacingOption2);
      const spacing = roundUpToMultipleOf5(spacingCalculated);
      
      // Отступы первого и последнего: не менее D_внутр(корпуса)
      const edgeOffsetCalculated = innerCorpusDiameter;
      const edgeOffset = roundUpToMultipleOf5(edgeOffsetCalculated);
      
      // Расчет необходимой высоты для размещения всех подключений
      const connectionsPerSide = Math.floor(connectionsCount / 2);
      
      // Если одно подключение на стороне, то минимальная высота = 2 * edgeOffset
      // Если больше, то высота = 2 * edgeOffset + (количество - 1) * spacing
      let calculatedHeight;
      if (connectionsPerSide === 1) {
        calculatedHeight = 2 * edgeOffset;
      } else {
        calculatedHeight = 2 * edgeOffset + (connectionsPerSide - 1) * spacing;
      }
      
      // Минимальная высота = максимум из расчётной и 3.5 * D_внутр(корпуса) (для гидравлического разделения)
      const minHeightForHydraulics = roundUpToMultipleOf5(innerCorpusDiameter * 3.5);
      minHeight = Math.max(calculatedHeight, minHeightForHydraulics);
      
      leftFirstOffset = edgeOffset;
      leftLastOffset = edgeOffset;
      leftSpacing = spacing;
      rightFirstOffset = edgeOffset;
      rightLastOffset = edgeOffset;
      rightSpacing = spacing;
    }
    
    // Расчет позиций подключений
    const connectionsPerSide = Math.floor(connectionsCount / 2);
    
    // Формируем массив подключений
    const leftConnections = [];
    const rightConnections = [];
    
    for (let i = 0; i < connectionsPerSide; i++) {
      const leftOffset = leftFirstOffset + (i * leftSpacing);
      const rightOffset = rightFirstOffset + (i * rightSpacing);
      
      leftConnections.push({
        id: `left-${i + 1}`,
        fittingId: getFittingIdFromConnection(characteristics.connectionType, characteristics.connectionSize),
        fittingLength: null,
        offsetFromTop: Math.round(leftOffset),
        offsetDirection: 'top',
        spacingToNext: null,
      });
      
      rightConnections.push({
        id: `right-${i + 1}`,
        fittingId: getFittingIdFromConnection(characteristics.connectionType, characteristics.connectionSize),
        fittingLength: null,
        offsetFromTop: Math.round(rightOffset),
        offsetDirection: 'top',
        spacingToNext: null,
      });
    }
    
    // Формируем параметры для редактора гидроразделителя
    const isSquareShape = characteristics.shape === 'square';
    const roundCapType =
      findStraightCap(selectedDiameter)
        ? getSeparatorCapTypeFromPressure('round', characteristics.pressure)
        : 'elliptical';
    const defaultEndCapMuftaId =
      typeof effectivePower === 'number' &&
      !Number.isNaN(effectivePower) &&
      effectivePower >= 1000
        ? 'Mufta_1'
        : 'Mufta_1.2';
    const params = {
      shape: isSquareShape ? 'square' : 'round',
      ...(isSquareShape
        ? { mainPipeProfile: `${selectedDiameter}x${selectedDiameter}`, mainPipeWallThickness: `x${selectedWallThickness}` }
        : { mainPipeDiameter: selectedDiameter, mainPipeWallThickness: `x${selectedWallThickness}` }
      ),
      separatorHeight: Math.round(minHeight),
      
      // Заглушки: квадрат — прямые; круг — прямая или эллиптическая по выбранному давлению
      hasTopCap: true,
      hasBottomCap: true,
      topCapElementType: 'cap',
      bottomCapElementType: 'cap',
      topCapType: isSquareShape ? 'straight' : roundCapType,
      bottomCapType: isSquareShape ? 'straight' : roundCapType,
      
      // Торцевые подключения
      hasTopConnection: true,
      topCapFittingCategory: 'Муфты',
      topCapFitting: defaultEndCapMuftaId,
      topCapFittingLength: null,
      hasBottomConnection: true,
      bottomCapFittingCategory: 'Муфты',
      bottomCapFitting: defaultEndCapMuftaId,
      bottomCapFittingLength: null,
      
      // Режим размещения и подключения
      connectionsPlacementMode: 'spacing',
      leftConnectionsFirstOffset: leftFirstOffset,
      leftConnectionsFirstOffsetMode: 'fromEdge',
      leftConnectionsLastOffset: leftLastOffset,
      leftConnectionsSpacing: Math.round(leftSpacing),
      leftConnections: leftConnections,
      
      rightConnectionsFirstOffset: rightFirstOffset,
      rightConnectionsFirstOffsetMode: 'fromEdge',
      rightConnectionsLastOffset: rightLastOffset,
      rightConnectionsSpacing: Math.round(rightSpacing),
      rightConnections: rightConnections,
      
      additionalOutlets: [],
      
      // Дополнительная информация для справки
      _calculatedFrom: 'characteristics',
      _calculatorViewMode: viewMode,
      _characteristics: characteristics,
    };
    
    // Передаем рассчитанные параметры
    onCalculate(params);
  };

  /** Наружный ⌀ круглого корпуса для проверки каталога заглушек (выбранный или авто-подбор). */
  const engineerRoundOuterDForCapCatalog = (() => {
    if (viewMode !== 'engineers' || characteristics.shape !== 'round') return null;
    const fromKey = parseRoundSeparatorOuterDiameter(characteristics.separatorDiameter);
    if (fromKey != null) return fromKey;
    if (!hasAvailableDiameters()) return null;
    const opt = getOptimalDiameter();
    return opt?.outerDiameter ?? null;
  })();
  const roundPipeNoStraightCapInCatalog =
    engineerRoundOuterDForCapCatalog != null &&
    !findStraightCap(engineerRoundOuterDForCapCatalog);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
        py: 3,
      }}
    >
      <Container maxWidth="md">
        {/* Шапка */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
              <IconButton
                onClick={onBack}
                sx={{
                  bgcolor: 'white',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <HomeIcon />
              </IconButton>
              {onBackToDrawing && (
                <IconButton
                  onClick={onBackToDrawing}
                  sx={{
                    bgcolor: 'white',
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  <ArrowForwardIcon />
                </IconButton>
              )}
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Расчет по характеристикам
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Гидравлический разделитель
              </Typography>
            </Box>
          </Box>

          {/* Переключатель режима — только для администратора */}
          {isAdmin && (
            <Tooltip title="Переключение между таблицами для клиентов и инженеров">
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, val) => { if (val) setViewMode(val); }}
                size="small"
                sx={{ bgcolor: 'white', boxShadow: 1 }}
              >
                <ToggleButton value="clients" sx={{ px: 2, textTransform: 'none', fontSize: '0.8rem' }}>
                  Для клиентов
                </ToggleButton>
                <ToggleButton value="engineers" sx={{ px: 2, textTransform: 'none', fontSize: '0.8rem' }}>
                  Для инженеров
                </ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
          )}
        </Box>

        {/* Форма ввода характеристик */}
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Входные характеристики
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            {/* Переключатель режима ввода */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Режим ввода:
                </Typography>
                <ToggleButtonGroup
                  value={characteristics.inputMode}
                  exclusive
                  onChange={(e, newMode) => {
                    if (newMode !== null) {
                      updateCharacteristic('inputMode', newMode);
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="power">
                    По мощности
                  </ToggleButton>
                  <ToggleButton value="flowRate">
                    По расходу
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>
            
            {/* Первая строка: Сечение */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Сечение</InputLabel>
                <Select
                  value={characteristics.shape}
                  label="Сечение"
                  onChange={(e) => updateCharacteristic('shape', e.target.value)}
                >
                  <MenuItem value="round">Круглое</MenuItem>
                  <MenuItem value="square">Квадратное</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Вторая строка: мощность/расход, расчётное поле, ΔT (у клиентов ΔT — третьим) */}
            {viewMode === 'engineers' && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Разница температур, °C (ΔT)</InputLabel>
                  <Select
                    value={characteristics.deltaTemp}
                    label="Разница температур, °C (ΔT)"
                    onChange={(e) => updateCharacteristic('deltaTemp', Number(e.target.value))}
                  >
                    {CLIENT_THERMAL_DELTA_TEMP_CHOICES.map((v) => (
                      <MenuItem key={v} value={v}>{v}°C</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {/* Расход или мощность (ввод) */}
            <Grid item xs={12} sm={4}>
              {characteristics.inputMode === 'flowRate' ? (
                <TextField
                  label="Расход теплоносителя, м³/ч"
                  type="number"
                  fullWidth
                  value={characteristics.flowRate === '' ? '' : characteristics.flowRate}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') {
                      updateCharacteristic('flowRate', '');
                      return;
                    }
                    const n = parseFloat(v);
                    if (!Number.isNaN(n)) updateCharacteristic('flowRate', n);
                  }}
                  onBlur={(e) => {
                    const n = parseFloat(e.target.value);
                    const maxP = getMaxAvailablePower();
                    const maxQ = calculateFlowRateFromPower(maxP, effectiveDeltaTemp, characteristics.fluid);
                    if (Number.isNaN(n) || n <= 0) {
                      const fb = Math.min(1, maxQ);
                      updateCharacteristic('flowRate', parseFloat(fb.toFixed(2)));
                      return;
                    }
                    const clamped = Math.min(Math.max(n, 0.1), maxQ);
                    updateCharacteristic('flowRate', parseFloat(clamped.toFixed(2)));
                  }}
                  inputProps={{
                    min: 0.1,
                    step: 0.1,
                  }}
                  helperText={getDiameterHintText()}
                  error={!hasAvailableDiameters()}
                  FormHelperTextProps={{
                    sx: {
                      color: !hasAvailableDiameters() ? 'error.main' : 'text.secondary'
                    }
                  }}
                />
              ) : viewMode === 'clients' ? (
                // Для клиентов: при уходе с поля — только ограничение диапазона (пресет по расходу)
                <TextField
                  label="Тепловая мощность, кВт"
                  type="number"
                  fullWidth
                  value={characteristics.power === '' ? '' : characteristics.power}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      updateCharacteristic('power', '');
                      return;
                    }
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) updateCharacteristic('power', numValue);
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    const minPower = 40;
                    const maxPower = getMaxAvailablePower();
                    if (value === '') {
                      updateCharacteristic('power', minPower, { finalizeClientPreset: true });
                      return;
                    }
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                      const clamped = clampClientSeparatorPowerKw(
                        numValue,
                        maxPower,
                        characteristics.shape
                      );
                      updateCharacteristic('power', clamped, { finalizeClientPreset: true });
                    }
                  }}
                  inputProps={{ min: CLIENT_POWER_STEPS[0], step: 1 }}
                  helperText={
                    getDiameterHintText() ||
                    `От ${CLIENT_POWER_STEPS[0]} до ${getMaxAvailablePower()} кВт`
                  }
                  error={Boolean(getDiameterHintText())}
                  FormHelperTextProps={{
                    sx: getDiameterHintText() ? { color: 'error.main' } : undefined,
                  }}
                />
              ) : (
                // Для инженеров: свободный ввод
                <TextField
                  label="Тепловая мощность, кВт"
                  type="number"
                  fullWidth
                  value={characteristics.power === '' ? '' : characteristics.power}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      updateCharacteristic('power', '');
                      return;
                    }
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                      updateCharacteristic('power', numValue);
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    const minPower = 40;
                    const maxPower = getMaxAvailablePower();
                    if (value === '') {
                      updateCharacteristic('power', minPower);
                      return;
                    }
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                      const clampedValue = Math.min(Math.max(numValue, minPower), maxPower);
                      updateCharacteristic('power', clampedValue);
                    }
                  }}
                  inputProps={{
                    min: 40,
                    max: getMaxAvailablePower(),
                    step: 1,
                  }}
                  helperText={getDiameterHintText() || `От 40 до ${getMaxAvailablePower()} кВт`}
                  error={!hasAvailableDiameters()}
                  FormHelperTextProps={{
                    sx: {
                      color: !hasAvailableDiameters() ? 'error.main' : 'text.secondary',
                    },
                  }}
                />
              )}
            </Grid>
            
            {/* Поле 3: Рассчитанная мощность или расход (вывод) */}
            <Grid item xs={12} sm={4}>
              {characteristics.inputMode === 'flowRate' ? (
                <TextField
                  label="Рассчитанная мощность, кВт"
                  type="text"
                  fullWidth
                  value={calculatePowerFromFlowRate(
                    characteristics.flowRate,
                    effectiveDeltaTemp,
                    characteristics.fluid
                  ).toFixed(2)}
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText=" "
                  FormHelperTextProps={{ sx: { m: 0, minHeight: '1.25rem' } }}
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              ) : (
                <TextField
                  label="Рассчитанный расход, м³/ч"
                  type="text"
                  fullWidth
                  value={(() => {
                    if (viewMode === 'clients' && clientThermalFlowM3h != null) {
                      return clientThermalFlowM3h.toFixed(2);
                    }
                    return calculateFlowRateFromPower(
                      characteristics.power,
                      effectiveDeltaTemp,
                      characteristics.fluid
                    ).toFixed(2);
                  })()}
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText=" "
                  FormHelperTextProps={{ sx: { m: 0, minHeight: '1.25rem' } }}
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              )}
            </Grid>

            {viewMode === 'clients' && (
              <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <FormControl fullWidth>
                  <InputLabel>Разница температур, °C (ΔT)</InputLabel>
                  <Select
                    value={normalizeClientThermalDeltaTemp(characteristics.deltaTemp)}
                    label="Разница температур, °C (ΔT)"
                    disabled={clientUsesPresetDeltaTemp(characteristics)}
                    onChange={(e) => updateCharacteristic('deltaTemp', Number(e.target.value))}
                  >
                    {CLIENT_THERMAL_DELTA_TEMP_CHOICES.map((v) => (
                      <MenuItem key={v} value={v}>{v}°C</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText component="div" sx={{ mx: 0, mt: 0.5, minHeight: '1.25rem' }}>
                    <FormControlLabel
                      sx={{ m: 0, alignItems: 'center' }}
                      control={
                        <Checkbox
                          size="small"
                          checked={clientUsesPresetDeltaTemp(characteristics)}
                          onChange={(e) =>
                            updateCharacteristic('clientPresetDeltaTemp', e.target.checked)
                          }
                        />
                      }
                      label={CLIENT_STANDARD_PRODUCTS_DELTA_TEMP_LABEL}
                    />
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}
            
            {/* Третья строка: Давление и Толщина стали — только для инженеров */}
            {viewMode === 'engineers' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Давление</InputLabel>
                  <Select
                    value={characteristics.pressure}
                    label="Давление"
                    onChange={(e) => updateCharacteristic('pressure', e.target.value)}
                  >
                    <MenuItem 
                      value={4} 
                      sx={{ display: characteristics.shape === 'round' ? 'none' : 'block' }}
                    >
                      4 бар рабочее / 6 бар испытательное
                    </MenuItem>
                    
                    <MenuItem 
                      value={6} 
                      disabled={
                        characteristics.shape === 'square' ||
                        (characteristics.shape === 'round' && roundPipeNoStraightCapInCatalog)
                      }
                    >
                      6 бар рабочее / 8 бар испытательное
                      {characteristics.shape === 'square' && ' (только для круглого)'}
                    </MenuItem>
                    
                    <MenuItem 
                      value={8} 
                      disabled={characteristics.shape === 'square'}
                    >
                      8 бар рабочее / 10 бар испытательное
                      {characteristics.shape === 'square' && ' (только для круглого)'}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {viewMode === 'engineers' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Заглушка"
                  value={getSeparatorCapDisplayLabel(characteristics.shape, characteristics.pressure)}
                  fullWidth
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="От выбранного давления"
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
            )}
            
            {/* Четвертая строка: Диаметр/Профиль гидроразделителя и Скорость */}
            <Grid item xs={12} sm={viewMode === 'engineers' ? 6 : 12}>
              {viewMode === 'engineers' ? (
                // Для инженеров: полноценный Select с выбором
                <FormControl fullWidth>
                  <InputLabel>
                    {characteristics.shape === 'square' ? 'Профиль гидроразделителя' : 'Диаметр гидроразделителя'}
                  </InputLabel>
                  <Select
                    value={(() => {
                      const currentValue = characteristics.separatorDiameter;
                      if (!currentValue) return '';
                      const available = getFilteredDiameters();
                      const exists = available.some(d => 
                        (d.profile ? `${d.profile}-${d.wallThickness}` : `${d.outerDiameter}-${d.wallThickness}`) === currentValue
                      );
                      return exists ? currentValue : '';
                    })()}
                    label={characteristics.shape === 'square' ? 'Профиль гидроразделителя' : 'Диаметр гидроразделителя'}
                    onChange={(e) => updateCharacteristic('separatorDiameter', e.target.value)}
                  >
                    {characteristics.shape === 'square' ? (
                      getFilteredDiameters().map(({ profile, wallThickness }) => {
                        const key = `${profile}-${wallThickness}`;
                        const size = parseInt(profile.split('x')[0]);
                        const innerSize = size - 2 * wallThickness;
                        const squareAreaM2 = (innerSize / 1000) * (innerSize / 1000);
                        const equivalentDiameterM = Math.sqrt((4 * squareAreaM2) / Math.PI);
                        const equivalentDiameterMm = (equivalentDiameterM * 1000).toFixed(1);
                        return (
                          <MenuItem key={key} value={key}>
                            {profile} x{wallThickness} (внутр. {innerSize}x{innerSize}, экв. ⌀{equivalentDiameterMm})
                          </MenuItem>
                        );
                      })
                    ) : (
                      getFilteredDiameters().map(({ outerDiameter, wallThickness }) => {
                        const key = `${outerDiameter}-${wallThickness}`;
                        const innerDiameter = outerDiameter - 2 * wallThickness;
                        return (
                          <MenuItem key={key} value={key}>
                            ⌀{outerDiameter} x{wallThickness} (внутр. ⌀{innerDiameter})
                          </MenuItem>
                        );
                      })
                    )}
                  </Select>
                </FormControl>
              ) : (
                // Для клиентов: только информационное поле (без выбора)
                <TextField
                  label={characteristics.shape === 'square' ? 'Профиль гидроразделителя' : 'Диаметр гидроразделителя'}
                  value={(() => {
                    const cp = CLIENT_PARAMS_TABLE[effectivePower];
                    if (cp) {
                      if (characteristics.shape === 'square') {
                        if (!cp.squareProfile) return 'Будет добавлено позже';
                        const wt = cp.squareWallThickness || 2;
                        const innerSize = parseInt(cp.squareProfile.split('x')[0]) - 2 * wt;
                        return `${cp.squareProfile} x${wt} (внутр. ${innerSize}x${innerSize})`;
                      }
                      const inner = cp.diameter - 2 * cp.wallThickness;
                      return `⌀${cp.diameter} x${cp.wallThickness} (внутр. ⌀${inner})`;
                    }
                    const val = characteristics.separatorDiameter;
                    if (!val) return 'Определяется автоматически';
                    if (characteristics.shape === 'square') {
                      const [profile, wt] = val.split('-');
                      return `${profile} x${wt}`;
                    } else {
                      const [od, wt] = val.split('-');
                      const inner = parseFloat(od) - 2 * parseInt(wt);
                      return `⌀${od} x${wt} (внутр. ⌀${inner})`;
                    }
                  })()}
                  fullWidth
                  disabled
                  InputProps={{ readOnly: true }}
                  helperText="Подбирается автоматически по мощности"
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              )}
            </Grid>
            
            {viewMode === 'engineers' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Скорость в корпусе, м/с"
                  value={getCurrentVelocity()}
                  fullWidth
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="В корпусе разделителя (0.09–0.25 м/с)"
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
            )}
            
            {/* Пятая строка: Подключения */}
            <Grid item xs={12} sm={viewMode === 'engineers' || !clientCatalogConnectionPresetOn ? 4 : 6}>
              <FormControl fullWidth>
                <InputLabel>Подключения</InputLabel>
                <Select
                  value={(() => {
                    const currentType = characteristics.connectionType;
                    return isConnectionTypeAvailable(currentType) ? currentType : '';
                  })()}
                  label="Подключения"
                  disabled={clientCatalogConnectionPresetOn}
                  onChange={(e) => updateCharacteristic('connectionType', e.target.value)}
                >
                  {isConnectionTypeAvailable('nipple') && (
                    <MenuItem value="nipple">Ниппели</MenuItem>
                  )}
                  {isConnectionTypeAvailable('coupling') && (
                    <MenuItem value="coupling">Муфты</MenuItem>
                  )}
                  {isConnectionTypeAvailable('flange') && (
                    <MenuItem value="flange">Фланцы</MenuItem>
                  )}
                  {isConnectionTypeAvailable('nut') && (
                    <MenuItem value="nut">Накидные гайки</MenuItem>
                  )}
                  {isConnectionTypeAvailable('washer') && (
                    <MenuItem value="washer">Шайбы с нар. резьбой</MenuItem>
                  )}
                </Select>
                {viewMode === 'clients' && (
                  <FormHelperText component="div" sx={{ mx: 0, mt: 0.5, minHeight: '1.25rem' }}>
                    <FormControlLabel
                      sx={{ m: 0, alignItems: 'center' }}
                      control={
                        <Checkbox
                          size="small"
                          checked={clientUsesPresetConnectionType(characteristics)}
                          onChange={(e) =>
                            updateCharacteristic('clientPresetConnectionType', e.target.checked)
                          }
                        />
                      }
                      label={CLIENT_STANDARD_PRODUCTS_CONNECTION_LABEL}
                    />
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={viewMode === 'engineers' || !clientCatalogConnectionPresetOn ? 4 : 6}>
              {clientCatalogConnectionPresetOn ? (
                <TextField
                  label="Размер подключений"
                  value={(() => {
                    const size = characteristics.connectionSize;
                    if (!size) return '—';
                    const found = connectionSizes.find((s) => s.diameter === size);
                    return found ? found.displayName : `⌀${size} мм`;
                  })()}
                  fullWidth
                  disabled
                  InputProps={{ readOnly: true }}
                  helperText="Из строки каталога (по расходу / конструкции)"
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Размер подключений</InputLabel>
                  <Select
                    value={(() => {
                      const currentSize = characteristics.connectionSize;
                      if (!currentSize) return '';
                      const available = getFilteredConnectionSizes();
                      const exists = available.some((s) => s.diameter === currentSize);
                      return exists ? currentSize : '';
                    })()}
                    label="Размер подключений"
                    onChange={(e) => updateCharacteristic('connectionSize', e.target.value)}
                  >
                    {getFilteredConnectionSizes().map((sizeData) => {
                      const { diameter, displayName } = sizeData;
                      const v = getConnectionVelocityForCatalogSize(sizeData);
                      return (
                        <MenuItem key={diameter} value={diameter}>
                          {displayName}{v > 0 ? ` (${v.toFixed(2)} м/с)` : ''}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  <FormHelperText>
                    {getFilteredConnectionSizes().length === 0
                      ? 'Нет подходящих размеров в диапазоне 0.3–1.5 м/с'
                      : `Скорость: ${getCurrentConnectionVelocity()} м/с (0.3–1.5)`}
                  </FormHelperText>
                </FormControl>
              )}
            </Grid>

            {(viewMode === 'engineers' || (viewMode === 'clients' && !clientCatalogConnectionPresetOn)) && (
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Скорость в подключениях, м/с"
                  value={getCurrentConnectionVelocity()}
                  fullWidth
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="(0.3–1.5 м/с)"
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Кнопка расчета */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<PlayArrowIcon />}
            onClick={calculateParameters}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: '1.1rem',
              fontWeight: 600,
            }}
          >
            Рассчитать параметры
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default CharacteristicsCalculator;
